import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Upload, X, ChevronLeft, Trash2, Plus, FileText, Calendar, User, Key, FileCheck, Zap, Home as HomeIcon, BarChart, Home, Download, Settings, ArrowRight } from 'lucide-react';
import styles from './PropertyInventoryDark.module.css';
import SignatureCanvas from 'react-signature-canvas'; // Uncommented
import type { InventoryItem, Signature, RoomPhoto } from '../types'; // Removed KeyEntry from here
import InventoryLoadingModal from './InventoryLoadingModal';
// import DatePicker from 'react-datepicker'; // Still commented
// import 'react-datepicker/dist/react-datepicker.css'; // Still commented
// import logo from '../assets/logo.png'; // Still commented
// import { jsPDF } from 'jspdf'; // Still commented
// import 'jspdf-autotable'; // Still commented
// import PropertyDetailsForm, { PropertyDetails } from './PropertyDetailsForm'; // Child component
// import CustomInput, { CustomInputProps } from './CustomInput'; // Child component
// import CustomDateInput, { CustomDateInputProps } from './CustomDateInput'; // Child component
import { compressImage, calculateImageSize } from '../utils/imageUtils'; // Uncommented utils
// import RoomInventoryItem, { RoomInventoryItemProps } from './RoomInventoryItem'; // Child component
// import KeyEntryItem, { KeyEntry } from './KeyEntryItem'; // Child component
import SignatureModal from './SignatureModal'; // Uncommented
import FinalSignatureModal from './FinalSignatureModal'; // Uncommented
import { useDebug } from '../hooks/useDebug'; // Uncommented simple hook
import { debounce, useDebouncedEffect } from '../utils/helpers'; // Uncommented utils
import { useSubscription } from '../hooks/useSubscription';
import { generateInventoryPDF } from '../utils/pdfGenerator'; // Uncommented
import ErrorBoundary from './ErrorBoundary';
import { 
  // conditionDescriptions, // Still keep these commented if not immediately used
  // cleanlinessDescriptions,
  defaultRooms,
  initialPropertyDetails,
  initialSignatures,
  initialCertificates,
  initialMeterReadings,
  initialKeys,
  // conditionOptions // Still keep these commented if not immediately used
} from '../constants/inventoryConstants'; // Ensure this path is correct
import { useIOSEnhancements } from '../hooks/useIOSEnhancements'; // Ensure this path is correct
import { useScrollManagement } from '../hooks/useScrollManagement'; // Ensure this path is correct
import { 
  renderRoomCameraButton as renderRoomCameraButtonUtil,
  renderRoomUploadButton as renderRoomUploadButtonUtil
} from '../utils/inventoryRenderUtils';
import { usePhotoHandling } from '../hooks/usePhotoHandling'; // Uncommented hook
import { loadPropertyInventoryFromFile, SavedPropertyInventoryData, savePropertyInventoryToFile } from '../utils/fileUtils'; // Import new util
import { saveInventoryToSupabase, loadInventoryFromSupabase, uploadPhotoToSupabase } from '../utils/supabaseStorage';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'; // For saving
import { useAutosave } from '../hooks/useAutosave'; // Add autosave import
import AutosaveIndicator from './AutosaveIndicator'; // Add autosave indicator import
import AddressInputModal from './AddressInputModal'; // Add address input modal import
import PhotoCapture from '../components/PhotoCapture';
import ContinuousCamera from '../components/ContinuousCamera';
import { PhotoData } from '../services/CameraService';
import CameraService from '../services/CameraService';
import { v4 as uuidv4 } from 'uuid';

import PropertyDetailsForm, { PropertyDetails } from './PropertyDetailsForm'; // Import type directly
import CustomInput, { CustomInputProps } from './CustomInput'; // Assuming CustomInput is used by PropertyDetailsForm or standalone
import CustomDateInput, { CustomDateInputProps as CustomDateInputPropsType } from './CustomDateInput'; // Assuming CustomDateInput is used
import KeyEntryItem, { KeyEntry as KeyEntryTypeFromItem } from './KeyEntryItem';
import TenantInput from './TenantInput';
import RoomInventoryItem, { RoomInventoryItemProps as RoomInventoryItemPropsType } from './RoomInventoryItem'; // Uncommented
import SectionPhotoGallery from './SectionPhotoGallery';
import type { CurrentPage } from '../App'; // Import CurrentPage

// Robust UUID generator function (same as in usePhotoHandling.ts)
const generateUUID = (): string => {
  try {
    // Try to use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Use imported uuid v4
    return uuidv4();
  } catch (error) {
    console.warn('[PropertyInventory] UUID libraries not available, using fallback generator:', error);
    // Fallback UUID generator
    return 'inv-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }
};

// Add a local KeyEntry type definition for now
interface KeyEntry {
  handedOver: string;
  type: string;
  quantity: string;
  photos?: RoomPhoto[];
}  // Add any other fields your initialKeys or KeyEntryItem component expect

// Keep the props interface as App.tsx expects it
export interface PropertyInventoryProps {
  onNavigate: (page: CurrentPage, associatedId?: string) => void;
  inventoryId?: string | null;
}

// Keep Room interface if it's simple and doesn't cause issues, or comment out for now
interface Room {
  name: string;
  items: InventoryItem[]; // Use the actual InventoryItem type now
  signatures?: Array<{
    type: 'tenant' | 'landlord';
    signature: string;
    date: string;
    name?: string;
  }>;
  // Mid-term report specific fields
  rating?: 1 | 2 | 3 | 4 | 5; // 1 = Excellent, 5 = Poor
  cleanliness?: 'professionally cleaned' | 'good domestic standard' | 'average domestic standard' | 'poor domestic standard' | 'not cleaned' | '';
}

// Define refs that were in the original code
const assessorSignatureRef = React.createRef<SignatureCanvas>(); // Changed to SignatureCanvas
const customerSignatureRef = React.createRef<SignatureCanvas>(); // Changed to SignatureCanvas
const tenantSignatureRef = React.createRef<SignatureCanvas>();   // Changed to SignatureCanvas
const landlordSignatureRef = React.createRef<SignatureCanvas>(); // Changed to SignatureCanvas
const containerRef = React.createRef<HTMLDivElement>();
const scrollTimeoutRef = React.createRef<number | null>(); // For scroll management effect
const activeInputRef = React.createRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>();
const scrollPositionRef = React.createRef<number>();
const finalSignatureModalRef = React.createRef<SignatureCanvas>(); // For final signature modal, changed to SignatureCanvas

interface PhotoUpdateInstruction {
  _operation: 'UPDATE_PHOTO_AT_INDEX';
  photoIndex: number;
  dataUrl: string;
}

const INVENTORIES_DIR = 'inventories'; // Matches the one in fileUtils.ts

const PropertyInventory: React.FC<PropertyInventoryProps> = ({ onNavigate, inventoryId }) => {
  // console.log('PropertyInventory - Testing iOS Scroll'); // Removed this log

  const { logEvent, DebugPanel } = useDebug();
  const { isStandardPlan } = useSubscription();
  // useIOSEnhancements(); // <--- Temporarily Comment This Out
  useScrollManagement({ 
    fixedHeaderSelector: '.inventory-page-header', 
    inventoryContainerSelector: '.inventory-main-content'
  });

  const [reportType, setReportType] = useState<'checkIn' | 'checkOut' | 'midTerm'>('checkIn');
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [signatures, setSignatures] = useState<Signature>(initialSignatures);
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>(initialPropertyDetails);
  const [rooms, setRooms] = useState<Room[]>(defaultRooms);
  const [certificates, setCertificates] = useState(initialCertificates);
  const [meterReadings, setMeterReadings] = useState(initialMeterReadings);
  const [keysData, setKeysData] = useState<KeyEntry[]>(initialKeys);
  
  const [activeRoom, setActiveRoom] = useState<number>(rooms.length > 0 ? 0 : 0);
  const [showSignatureModal, setShowSignatureModal] = useState(false); // State for general signature modal
  const [currentSignatureType, setCurrentSignatureType] = useState<'tenant' | 'landlord' | 'assessor' | 'customer'>('tenant'); // Expanded type
  const [currentRoomIndexForSig, setCurrentRoomIndexForSig] = useState(0); // For room-specific signatures
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});
  const [isFinalSignatureModalOpen, setIsFinalSignatureModalOpen] = useState(false); // State for final signature modal
  const [isInventoryLoading, setIsInventoryLoading] = useState<boolean>(true); // New state for loading
  const [loadingStage, setLoadingStage] = useState<'loading' | 'processing' | 'complete'>('loading'); // Loading stage
  const [currentInventoryId, setCurrentInventoryId] = useState<string | null>(inventoryId || null);
  const [isSaving, setIsSaving] = useState(false);
  const [tempInventoryId, setTempInventoryId] = useState<string | null>(null); // Add temporary ID for new inventories
  // Initial state for modal: show if no inventoryId AND no address yet from a potentially loaded inventory.
  // This will be further refined by the useEffect below.
  const [showAddressModal, setShowAddressModal] = useState<boolean>(false);
  const initialModalCheckDoneRef = useRef<boolean>(false);

  // Processing state for immediate feedback
  const [processingItems, setProcessingItems] = useState<Set<string>>(new Set());
  const [isUploadingPhotos, setIsUploadingPhotos] = useState<boolean>(false);
  const uploadTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper functions for processing state
  const startProcessing = (roomIndex: number, itemIndex: number) => {
    const key = `${roomIndex}-${itemIndex}`;
    setProcessingItems(prev => new Set(prev).add(key));
    
    // Show full-screen loading overlay for photo uploads
    setIsUploadingPhotos(true);
    console.log('[PropertyInventory] Photo upload started, showing loading overlay');
    
    // Clear any existing timer
    if (uploadTimerRef.current) {
      clearTimeout(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }
  };
  
  const stopProcessing = (roomIndex: number, itemIndex: number) => {
    const key = `${roomIndex}-${itemIndex}`;
    setProcessingItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      
      // Hide loading overlay only when all uploads are complete
      if (newSet.size === 0) {
        console.log('[PropertyInventory] All photo uploads complete, hiding loading overlay');
        // Delay hiding slightly to show completion state
        uploadTimerRef.current = setTimeout(() => {
          setIsUploadingPhotos(false);
        }, 500);
      }
      
      return newSet;
    });
  };
  
  const isProcessing = (roomIndex: number, itemIndex: number) => {
    const key = `${roomIndex}-${itemIndex}`;
    return processingItems.has(key);
  };
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (uploadTimerRef.current) {
        clearTimeout(uploadTimerRef.current);
      }
    };
  }, []);

  // ✅ Use refs to always have latest state available to autosave
  const roomsRef = useRef(rooms);
  const propertyDetailsRef = useRef(propertyDetails);
  const certificatesRef = useRef(certificates);
  const meterReadingsRef = useRef(meterReadings);
  const keysDataRef = useRef(keysData);
  const signaturesRef = useRef(signatures);
  const reportTypeRef = useRef(reportType);

  // Keep refs in sync with state
  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);

  useEffect(() => {
    propertyDetailsRef.current = propertyDetails;
  }, [propertyDetails]);

  useEffect(() => {
    certificatesRef.current = certificates;
  }, [certificates]);

  useEffect(() => {
    meterReadingsRef.current = meterReadings;
  }, [meterReadings]);

  useEffect(() => {
    keysDataRef.current = keysData;
  }, [keysData]);

  useEffect(() => {
    signaturesRef.current = signatures;
  }, [signatures]);

  useEffect(() => {
    reportTypeRef.current = reportType;
  }, [reportType]);

  // Calculate hasUnsavedChanges based on actual pending operations or data diff
  const calculateHasUnsavedChanges = useCallback(() => {
    // Basic check: if any photo is currently uploading or has an error
    for (const room of rooms) {
      for (const item of room.items) {
        if (item.photos.some(p => p.uploading || p.error)) {
          return true;
        }
      }
    }
    // More sophisticated diffing for other data can be added here if needed.
    // For now, if autosave is enabled, we assume other interactions call markAsChanged.
    // This primarily helps autosave continue if photo uploads are pending.
    return false; // Default to false if no obvious pending photo ops
  }, [rooms]);

  const autosave = useAutosave(
    {
      id: currentInventoryId || tempInventoryId, // Use temp ID if no current ID
      data: { /* data object assembled by getDataToSave */ },
      name: propertyDetails.address || `inventory-${Date.now()}`,
      // Let markAsChanged handle this, but provide a way for autosave to know if photo ops are pending
      hasUnsavedChanges: calculateHasUnsavedChanges(), 
    },
    {
      saveFunction: async (name: string, dataToSave: any, existingId?: string) => {
        // Ensure photos in dataToSave are just metadata (URLs, not base64)
        return await saveInventoryToSupabase(name, dataToSave, existingId);
      },
      getDataToSave: () => {
        // ✅ CRITICAL: Read from refs to get LATEST state, not captured closure state
        const roomsWithPhotoMetadata = roomsRef.current.map(room => ({
          ...room,
          items: room.items.map(item => ({
            ...item,
            photos: item.photos.map(p => ({
              id: p.id,
              fileName: p.fileName,
              url: p.url,
              localPath: p.localPath, // ✅ CRITICAL: Save for crash recovery
              caption: p.caption,
              timestamp: p.timestamp,
              error: p.error,
              order: p.order,
              uploading: p.uploading, // ✅ CRITICAL: Track upload status
              needsUpload: !p.url && p.localPath // ✅ Flag for recovery system
            }))
          }))
        }));      
        return {
          reportType: reportTypeRef.current,
          propertyDetails: propertyDetailsRef.current,
          rooms: roomsWithPhotoMetadata,
          certificates: certificatesRef.current,
          meterReadings: meterReadingsRef.current,
          keysData: keysDataRef.current,
          signatures: signaturesRef.current,
        };
      },
      getFileName: () => {
        // ✅ Read from ref to get latest address
        const currentAddress = propertyDetailsRef.current.address;
        if (!currentAddress || currentAddress.trim().length < 3) {
          return `inventory-${Date.now()}`;
        }
        return currentAddress.trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_').substring(0, 50);
      },
      validateBeforeSave: (context?: { address?: string }) => {
        const addressToValidate = context?.address !== undefined ? context.address : propertyDetailsRef.current.address;
        if (!addressToValidate || addressToValidate.trim().length < 3) {
          return { isValid: false, message: 'Property address must be at least 3 characters long' };
        }
        return { isValid: true };
      },
      onSaveSuccess: (savedId: string) => {
        // If we were using a temp ID and now have a permanent one, transition
        if (tempInventoryId && !currentInventoryId) {
          setTempInventoryId(null); // Clear temp ID
        }
        setCurrentInventoryId(savedId);
        // Potentially trigger a re-check of hasUnsavedChanges if uploads were completed
        autosave.markAsChanged(); // This will re-evaluate with new `calculateHasUnsavedChanges`
      },
      onSaveError: (error: Error) => console.error('[Autosave] Failed to save inventory:', error)
    },
    { enabled: false, intervalMs: 30000, debounceMs: 3000, maxRetries: 3 }
  );

  useEffect(() => {
    // If currentInventoryId changes (e.g. after first save), we need to ensure autosave hook knows.
    // The autosave hook re-evaluates its `autosaveData.id` via its own useEffect.
    // We also might need to re-evaluate `hasUnsavedChanges` if the ID change implies state change.
    autosave.markAsChanged();
  }, [currentInventoryId, tempInventoryId, autosave]);

  // Initialize tempInventoryId for new inventories
  useEffect(() => {
    if (!currentInventoryId && !tempInventoryId && !inventoryId) {
      // Generate a proper UUID for temporary inventory (without prefix)
      // We'll track this is temporary by not having it in currentInventoryId until first save
      const newTempId = generateUUID();
      setTempInventoryId(newTempId);
      console.log('[PropertyInventory] Generated temp inventory ID for new inventory:', newTempId);
    }
  }, [currentInventoryId, tempInventoryId, inventoryId]);

  const getItemPhotos = useCallback((roomIndex: number, itemIndex: number): RoomPhoto[] => {
    // Ensure rooms and items exist to prevent runtime errors
    if (rooms && rooms[roomIndex] && rooms[roomIndex].items && rooms[roomIndex].items[itemIndex]) {
      return rooms[roomIndex].items[itemIndex].photos || [];
    }
    return [];
  }, [rooms]);

  const getCurrentInventoryIdSafe = useCallback(() => {
    // If we have a current inventory ID, use it
    if (currentInventoryId) {
      return currentInventoryId;
    }
    
    // If we have a temp ID, use it
    if (tempInventoryId) {
      return tempInventoryId;
    }
    
    // Last resort: generate a temp ID synchronously
    // This shouldn't happen often due to the useEffect above, but provides a fallback
    const fallbackTempId = generateUUID();
    console.warn('[PropertyInventory] Generating fallback temp ID:', fallbackTempId);
    // Don't call setTempInventoryId here to avoid infinite loops
    return fallbackTempId;
  }, [currentInventoryId, tempInventoryId]);

  const updateRoomItem = useCallback((
    roomIndex: number,
    itemIndex: number,
    updates: Partial<InventoryItem> | { photoToUpdate: RoomPhoto }
  ) => {
    setRooms(prevRooms => {
      // Create a deep copy of the rooms array to avoid mutation
      const newRooms = prevRooms.map((room, rIndex) => {
        if (rIndex !== roomIndex) return room; // Return unchanged rooms
        
        // Deep copy the target room
        const newRoom = {
          ...room,
          items: room.items.map((item, iIndex) => {
            if (iIndex !== itemIndex) return item; // Return unchanged items
            
            // Deep copy the target item
            const newItem = { ...item };
            
            // Handle different types of updates
            if ('photoToUpdate' in updates) {
              const { photoToUpdate } = updates;
              
              // Create a new photos array
              const currentPhotos = [...(newItem.photos || [])];
              const photoIndex = currentPhotos.findIndex(p => p.id === photoToUpdate.id);
              
              if (photoIndex !== -1) {
                // Update existing photo
                currentPhotos[photoIndex] = { ...photoToUpdate };
                console.log(`[PropertyInventory] updateRoomItem: Updated existing photo at index ${photoIndex}:`, photoToUpdate.id);
              } else {
                // Add new photo
                currentPhotos.push({ ...photoToUpdate });
                console.log('[PropertyInventory] updateRoomItem: Added new photo:', photoToUpdate.id);
              }
              
              // Sort by order and assign
              newItem.photos = currentPhotos.sort((a, b) => (a.order || 0) - (b.order || 0));
              
            } else if ('photos' in updates && Array.isArray(updates.photos)) {
              // Batch photo update with special handling for deletions
              const newPhotos = updates.photos as RoomPhoto[];
              const currentPhotos = [...(newItem.photos || [])];
              
              console.log(`[PropertyInventory] updateRoomItem: Batch updating photos. Current: ${currentPhotos.length}, New: ${newPhotos.length}`);
              
              // Check if this is actually a deletion operation
              // True deletion: new array is smaller AND contains some IDs that exist in current array
              // False deletion (addition): new array contains completely new IDs not in current
              const newPhotoIds = new Set(newPhotos.map(p => p.id));
              const currentPhotoIds = new Set(currentPhotos.map(p => p.id));
              const missingPhotos = currentPhotos.filter(p => !newPhotoIds.has(p.id));
              const overlappingPhotos = newPhotos.filter(p => currentPhotoIds.has(p.id));
              
              // Only treat as deletion if there's overlap (some photos are preserved) AND some are missing
              const isActualDeletion = missingPhotos.length > 0 && overlappingPhotos.length > 0;
              
              if (isActualDeletion) {
                // For deletion operations, we use the new array directly
                console.log(`[PropertyInventory] updateRoomItem: Detected photo deletion (${missingPhotos.length} photos removed), using new array directly`);
                
                // Debug: Log photo IDs before and after for troubleshooting
                console.log(`[PropertyInventory] Current photos:`, currentPhotos.map(p => p.id));
                console.log(`[PropertyInventory] New photos (after deletion):`, newPhotos.map(p => p.id));
                console.log(`[PropertyInventory] Deleted photos:`, missingPhotos.map(p => p.id));
                
                newItem.photos = [...newPhotos];
              } else {
                // For additions or updates, use the existing merge logic
              // Create a map of current photos for efficient lookup
              const currentPhotoMap = new Map(currentPhotos.map(p => [p.id, p]));
              const updatedPhotoIds = new Set(newPhotos.map(p => p.id));
              
              // Merge logic:
              // 1. Keep photos that exist in current but not in new batch (concurrent uploads)
              // 2. Add/update photos from the new batch
              const preservedPhotos = currentPhotos.filter(p => !updatedPhotoIds.has(p.id));
              const mergedPhotos = [...preservedPhotos];
              
              // Add new photos, preserving any concurrent state changes
              newPhotos.forEach(newPhoto => {
                const existingPhoto = currentPhotoMap.get(newPhoto.id);
                if (existingPhoto) {
                  // Merge states: prefer upload status/error from existing if more recent
                  const mergedPhoto = {
                    ...newPhoto,
                    // Keep upload state if currently uploading (don't overwrite with batch state)
                    uploading: existingPhoto.uploading || newPhoto.uploading,
                    // Keep more recent error state
                    error: existingPhoto.error || newPhoto.error,
                    // Keep URL if available from either source
                    url: existingPhoto.url || newPhoto.url,
                    // Keep more recent timestamp
                    timestamp: (existingPhoto.timestamp && newPhoto.timestamp && existingPhoto.timestamp > newPhoto.timestamp) 
                      ? existingPhoto.timestamp 
                      : newPhoto.timestamp || existingPhoto.timestamp || new Date().toISOString()
                  };
                  mergedPhotos.push(mergedPhoto);
                } else {
                  mergedPhotos.push({ ...newPhoto });
                }
              });
              
              // Sort and assign
              newItem.photos = mergedPhotos.sort((a, b) => (a.order || 0) - (b.order || 0));
              }
              
              console.log(`[PropertyInventory] updateRoomItem: Final photo count after update: ${newItem.photos.length}`);
            } else {
              // Standard item property updates
              Object.assign(newItem, updates as Partial<InventoryItem>);
            }
            
            return newItem;
          })
        };
        
        return newRoom;
      });
      
      return newRooms;
    });
    
    // Debounced autosave trigger with increased delay to allow uploads to complete
    const timeoutId = setTimeout(() => {
      autosave.markAsChanged();
    }, 1500); // Increased from 500ms to 1500ms to give uploads more time and reduce save frequency
    
    // Cleanup timeout on unmount (though this callback is stable)
    return () => clearTimeout(timeoutId);
  }, [autosave]);

  const { 
    handleRoomPhotoUpload, 
    handleGallerySelect,
    handleCameraCapture
  } = usePhotoHandling({ 
    updateRoomItem,
    getItemPhotos,
    getCurrentInventoryId: getCurrentInventoryIdSafe,
    onProcessingStart: startProcessing,
    onProcessingStop: stopProcessing,
    onBatchUploadStart: autosave.suspendAutosave,
    onBatchUploadEnd: autosave.resumeAutosave
  });

  // Helper function to detect mobile devices
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window as any).Capacitor !== undefined;
  };

  // 🔄 CRASH RECOVERY: Check for pending photos and offer to retry upload
  const checkForPendingPhotos = async (loadedRooms: any[]) => {
    try {
      const { Dialog } = await import('@capacitor/dialog');
      
      // Count photos that need uploading (have localPath but no url)
      let pendingPhotoCount = 0;
      const pendingPhotos: Array<{ roomIndex: number; itemIndex: number; photo: RoomPhoto }> = [];
      
      loadedRooms.forEach((room, roomIndex) => {
        room.items?.forEach((item: InventoryItem, itemIndex: number) => {
          item.photos?.forEach((photo: RoomPhoto) => {
            if (photo.localPath && !photo.url && !photo.uploading) {
              pendingPhotoCount++;
              pendingPhotos.push({ roomIndex, itemIndex, photo });
            }
          });
        });
      });
      
      if (pendingPhotoCount > 0) {
        console.log(`🔄 [Crash Recovery] Found ${pendingPhotoCount} photos pending upload`);
        
        // Show recovery dialog
        const result = await Dialog.confirm({
          title: 'Recover Photos',
          message: `Found ${pendingPhotoCount} photo${pendingPhotoCount > 1 ? 's' : ''} that ${pendingPhotoCount > 1 ? 'weren\'t' : 'wasn\'t'} uploaded before the app closed. Would you like to retry uploading now?`
        });
        
        if (result.value) {
          console.log('🔄 [Crash Recovery] User accepted - retrying uploads');
          
          // Retry uploads for pending photos
          for (const { roomIndex, itemIndex, photo } of pendingPhotos) {
            try {
              // Mark as uploading
              updateRoomItem(roomIndex, itemIndex, {
                photoToUpdate: { ...photo, uploading: true, error: undefined }
              });
              
              // Load photo from local path and upload
              const photoDataUrl = photo.localPath;
              
              if (photoDataUrl) {
                const supabaseUrl = await uploadPhotoToSupabase(
                  photoDataUrl,
                  photo.fileName,
                  'inventory-photos'
                );
                
                // Update with cloud URL
                updateRoomItem(roomIndex, itemIndex, {
                  photoToUpdate: {
                    ...photo,
                    url: supabaseUrl,
                    uploading: false,
                    error: undefined,
                    localPath: undefined // Clear local path after successful upload
                  }
                });
                
                console.log(`✅ [Crash Recovery] Recovered photo: ${photo.fileName}`);
              }
            } catch (error) {
              console.error(`❌ [Crash Recovery] Failed to recover photo: ${photo.fileName}`, error);
              
              // Update with error
              updateRoomItem(roomIndex, itemIndex, {
                photoToUpdate: {
                  ...photo,
                  uploading: false,
                  error: `Recovery failed: ${(error as Error).message}`
                }
              });
            }
          }
          
          // Show completion message
          await Dialog.alert({
            title: 'Recovery Complete',
            message: `Successfully recovered ${pendingPhotoCount} photo${pendingPhotoCount > 1 ? 's' : ''}.`
          });
        } else {
          console.log('🔄 [Crash Recovery] User declined recovery');
        }
      }
    } catch (error) {
      console.error('[Crash Recovery] Error checking for pending photos:', error);
    }
  };

  // --- Loading Logic (useEffect for inventoryId) ---
  useEffect(() => {
    const loadInventoryData = async () => {
      if (inventoryId) {
        setIsInventoryLoading(true);
        setLoadingStage('loading');
        setCurrentInventoryId(inventoryId);
        try {
          let loadedData: SavedPropertyInventoryData | null = null;
          try {
            loadedData = await loadInventoryFromSupabase(inventoryId);
            console.log('[PropertyInventory] Loaded from Supabase:', loadedData);
          } catch (supabaseError) {
            console.warn('[PropertyInventory] Failed to load from Supabase, trying local file:', supabaseError);
        try {
                loadedData = await loadPropertyInventoryFromFile(inventoryId);
                console.log('[PropertyInventory] Loaded from Local File:', loadedData);
            } catch (localError) {
                console.error('[PropertyInventory] Failed to load from local file as well:', localError);
            }
          }
          
          // Update to processing stage
          setLoadingStage('processing');
          
          if (loadedData) {
            setReportType(loadedData.reportType || 'checkIn');
            setPropertyDetails(loadedData.propertyDetails || initialPropertyDetails);
            // Ensure photos in loadedData are compatible with RoomPhoto[] type or transform them
            const transformedRooms = (loadedData.rooms || defaultRooms).map(room => ({
              ...room,
              items: room.items.map(item => ({
                ...item,
                // If loaded photos are just strings (old format), map them to RoomPhoto with URL
                // Or if they are already partial RoomPhoto objects, ensure all needed fields are present
                photos: (item.photos || []).map((photo: any, index: number) => {
                  if (typeof photo === 'string') { // Old format: string was a base64 or URL
                    const photoId = generateUUID();
                    return {
                      id: photoId,
                      url: photo, // Assume it's a URL if it's a string from DB
                      fileName: `loaded_${inventoryId}_${room.name}_${item.category}_${index}.jpg`, // Placeholder filename
                      localPath: undefined,
                      uploading: false,
                      error: undefined,
                      timestamp: new Date().toISOString(),
                      order: index,
                    } as RoomPhoto;
                  }
                  // New format: photo is already an object with at least id, fileName, url
                  return {
                    localPath: undefined, // Ensure not present from DB
                    uploading: false, // Assume not uploading from DB
                    error: photo.error || undefined,
                    caption: photo.caption || '',
                    timestamp: photo.timestamp || new Date().toISOString(),
                    order: photo.order !== undefined ? photo.order : index,
                    ...photo, // Spread to include id, fileName, url, etc.
                  } as RoomPhoto;
                })
              })),
              // Transform signatures from old format (dataURL) to new format (signature)
              signatures: room.signatures?.map(sig => ({
                type: sig.type,
                signature: (sig as any).dataURL || (sig as any).signature || '',
                date: (sig as any).date || new Date().toISOString().split('T')[0],
                name: (sig as any).name
              })),
              // Add mid-term fields (will be undefined for check-in/check-out)
              rating: room.rating,
              cleanliness: room.cleanliness || ''
            })) as Room[];
            
            // Update to final stage
            setLoadingStage('complete');
            
            // Simulate a brief delay for the complete stage
            await new Promise(resolve => setTimeout(resolve, 500));
            
            setRooms(transformedRooms);
            setCertificates({
              gas: { ...initialCertificates.gas, ...(loadedData.certificates?.gas || {}) },
              electrical: { ...initialCertificates.electrical, ...(loadedData.certificates?.electrical || {}) }
            });
            setMeterReadings({
              gas: { ...initialMeterReadings.gas, ...(loadedData.meterReadings?.gas || {}) },
              electricity: { ...initialMeterReadings.electricity, ...(loadedData.meterReadings?.electricity || {}) },
              water: { ...initialMeterReadings.water, ...(loadedData.meterReadings?.water || {}) }
            });
            setKeysData(loadedData.keysData || initialKeys);
            setSignatures(loadedData.signatures || initialSignatures);
            setShowAddressModal(false);
            
            // 🔄 CRASH RECOVERY: Check for photos that need uploading
            setTimeout(async () => {
              await checkForPendingPhotos(transformedRooms);
            }, 1000); // Delay to avoid blocking UI 
            
            if (loadedData.propertyDetails?.address && loadedData.propertyDetails.address.trim().length >= 3) {
              autosave.enableAutosave({ address: loadedData.propertyDetails.address });
            } else {
              autosave.disableAutosave();
              // If loaded an inventory without an address, still hide main modal, user can add via indicator
            }
            console.log("[PropertyInventory] States updated from loaded inventory file.");
          } else {
            console.warn(`[PropertyInventory] No data returned for inventory ID: ${inventoryId}. Starting fresh.`);
            setCurrentInventoryId(null); // Reset if load fails fundamentally
            setPropertyDetails(initialPropertyDetails);
            setRooms(defaultRooms);
            setCertificates(initialCertificates);
            setMeterReadings(initialMeterReadings);
            setKeysData(initialKeys);
            setSignatures(initialSignatures);
            setShowAddressModal(false); 
          }
        } catch (error) {
          console.error(`[PropertyInventory] Error loading inventory ${inventoryId}:`, error);
          setCurrentInventoryId(null);
          setPropertyDetails(initialPropertyDetails);
          setRooms(defaultRooms);
          // ... reset other states ...
          setShowAddressModal(false); 
        } finally {
          setIsInventoryLoading(false);
          autosave.markAsChanged(); // Check for pending changes after load
        }
      } else {
        // No inventoryId, this is a new inventory
        setCurrentInventoryId(null);
        setPropertyDetails(initialPropertyDetails);
        setRooms(defaultRooms);
        // ... reset other states ...
        setIsInventoryLoading(false); // Ensure loading state is false for new inventories
        if (!initialModalCheckDoneRef.current) {
            setShowAddressModal(true); // Show modal for new inventories
        }
      }
      initialModalCheckDoneRef.current = true; 
    };
    loadInventoryData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId]); // Only re-run if inventoryId changes. 
  // `autosave` object removed, other state setters are stable.

  const handleAddressSubmit = (address: string) => {
    setPropertyDetails(prev => ({ ...prev, address }));
    setShowAddressModal(false);
    autosave.enableAutosave({ address });
    if (!currentInventoryId) { // If it's a new inventory, first save might create an ID
        autosave.triggerManualSave(); // Attempt to save to get an ID if needed for photo paths
    }
  };

  const handleSkipAddress = () => {
    setShowAddressModal(false);
    autosave.disableAutosave();
  };

  // ✅ IMMEDIATE SAVE: Listen for photo upload completion and trigger immediate save
  useEffect(() => {
    const handlePhotoUploadComplete = (event: CustomEvent) => {
      const { successCount, failCount, roomIndex, itemIndex } = event.detail;
      console.log(`[PropertyInventory] 💾 Photos uploaded (${successCount} success, ${failCount} failed) - triggering IMMEDIATE save`);
      
      // Force immediate save instead of waiting for debounce
      autosave.triggerManualSave();
    };
    
    window.addEventListener('inventory-photos-uploaded', handlePhotoUploadComplete as EventListener);
    
    return () => {
      window.removeEventListener('inventory-photos-uploaded', handlePhotoUploadComplete as EventListener);
    };
  }, [autosave]);

  // --- Export Modal State ---
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'html' | 'both'>('html');
  const [modalStep, setModalStep] = useState<'customization' | 'export'>('customization');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [headerColor, setHeaderColor] = useState<string>('#f59e0b');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [tempColor, setTempColor] = useState(headerColor);
  
  // Company details customization
  const [companyName, setCompanyName] = useState<string>('Energy Ltd');
  const [companyWebsite, setCompanyWebsite] = useState<string>('www.example.com');
  const [companyPhone, setCompanyPhone] = useState<string>('0800 123 4567');
  const [companyEmail, setCompanyEmail] = useState<string>('info@example.com');
  const [companyAddress, setCompanyAddress] = useState<string>('123 Business Street, London, EC2A 4DP');
  
  // Profile management
  interface CustomizationProfile {
    id: string;
    name: string;
    logoDataUrl: string | null;
    headerColor: string;
    companyName: string;
    companyWebsite: string;
    companyPhone: string;
    companyEmail: string;
    companyAddress: string;
    createdAt: number;
  }
  
  const [savedProfiles, setSavedProfiles] = useState<CustomizationProfile[]>([]);
  const [profileName, setProfileName] = useState<string>('');
  const [showSaveProfile, setShowSaveProfile] = useState(false);
  
  // Load saved profiles on mount
  useEffect(() => {
    const loadedProfiles = localStorage.getItem('inventory-export-profiles');
    if (loadedProfiles) {
      try {
        setSavedProfiles(JSON.parse(loadedProfiles));
      } catch (error) {
        console.error('Failed to load profiles:', error);
      }
    }
  }, []);

  // --- PDF Generation Handler ---
  const handleGenerateAndDownloadPdf = async (exportType: 'jspdf' | 'html' = 'html', options?: { logoDataUrl?: string; headerColor?: string; formatChoice?: 'pdf' | 'html' | 'both'; companyName?: string; companyWebsite?: string; companyPhone?: string; companyEmail?: string; companyAddress?: string }) => {
    logEvent('pdfGenerationAttempt', { reportType, exportType }, {});
    
    // DEBUG: Log room data to verify rating/cleanliness is present
    console.log('🔍 [PropertyInventory] Rooms data before PDF generation:', rooms.map((r, i) => ({
      index: i,
      name: r.name,
      rating: r.rating,
      cleanliness: r.cleanliness,
      itemCount: r.items?.length || 0
    })));
    
    try {
      await generateInventoryPDF({
        reportType,
        propertyDetails,
        rooms, // This needs to match the PdfRoom[] structure expected by generateInventoryPDF
        certificates,
        keys: keysData, // Pass keysData as keys
        meterReadings,
        signatures,
        exportType, // Pass the export type
        options, // Pass customization options
      });
      logEvent('pdfGenerationSuccess', { reportType, exportType }, {});
    } catch (error) {
      console.error("Error generating PDF:", error);
      logEvent('pdfGenerationError', { reportType, exportType, error: (error as Error).message }, {}, (error as Error));
      alert(`Failed to generate PDF: ${(error as Error).message}`);
    }
  };

  // --- Export Handler - shows modal ---
  const handleExportClick = () => {
    setShowExportModal(true);
    setModalStep('customization');
  };

  // --- Handle export with format selection ---
  const handleExport = async (format: 'pdf' | 'html' | 'both', options: { logoDataUrl?: string; headerColor?: string; companyName?: string; companyWebsite?: string; companyPhone?: string; companyEmail?: string; companyAddress?: string }) => {
    setShowExportModal(false);
    
    // Standard plan users: Only logo, no contact info
    if (isStandardPlan) {
      options.companyName = '';
      options.companyWebsite = '';
      options.companyPhone = '';
      options.companyEmail = '';
      options.companyAddress = '';
    }
    
    // Always use 'html' exportType, pass format choice through options
    await handleGenerateAndDownloadPdf('html', { 
      ...options, 
      formatChoice: format 
    });
  };

  // --- Logo handling ---
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setLogoPreview(e.target.result as string);
          setLogoFile(file);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    const fileInput = document.getElementById('inventory-logo-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  // --- Color picker ---
  const openColorPicker = () => {
    setTempColor(headerColor);
    setShowColorPicker(true);
  };

  const applyColor = () => {
    setHeaderColor(tempColor);
    setShowColorPicker(false);
  };

  const cancelColorChange = () => {
    setTempColor(headerColor);
    setShowColorPicker(false);
  };
  
  // --- Profile Management ---
  const saveProfile = () => {
    if (!profileName.trim()) {
      alert('Please enter a profile name');
      return;
    }
    
    const newProfile: CustomizationProfile = {
      id: Date.now().toString(),
      name: profileName.trim(),
      logoDataUrl: logoPreview,
      headerColor,
      companyName,
      companyWebsite,
      companyPhone,
      companyEmail,
      companyAddress,
      createdAt: Date.now()
    };
    
    const updatedProfiles = [...savedProfiles, newProfile];
    setSavedProfiles(updatedProfiles);
    localStorage.setItem('inventory-export-profiles', JSON.stringify(updatedProfiles));
    setProfileName('');
    setShowSaveProfile(false);
  };
  
  const loadProfile = (profile: CustomizationProfile) => {
    setLogoPreview(profile.logoDataUrl);
    setHeaderColor(profile.headerColor);
    setCompanyName(profile.companyName);
    setCompanyWebsite(profile.companyWebsite);
    setCompanyPhone(profile.companyPhone);
    setCompanyEmail(profile.companyEmail || 'info@example.com');
    setCompanyAddress(profile.companyAddress || '123 Business Street, London, EC2A 4DP');
    
    // Clear the file input since we're loading from saved data
    setLogoFile(null);
    const fileInput = document.getElementById('inventory-logo-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };
  
  const deleteProfile = (profileId: string) => {
    if (!confirm('Are you sure you want to delete this profile?')) return;
    
    const updatedProfiles = savedProfiles.filter(p => p.id !== profileId);
    setSavedProfiles(updatedProfiles);
    localStorage.setItem('inventory-export-profiles', JSON.stringify(updatedProfiles));
  };

  // --- Restore some simpler helper functions (add more as needed if they are simple) ---
  const updatePropertyDetail = (field: keyof PropertyDetails, value: string | string[] | number | boolean) => {
    setPropertyDetails((prev: PropertyDetails) => ({
      ...prev,
      [field]: value
    }));
    autosave.markAsChanged(); // Trigger autosave on property detail change
  };

  const addKeyEntry = () => {
    setKeysData(prevKeys => [...prevKeys, { handedOver: '', type: '', quantity: '1', photos: [] }]);
    autosave.markAsChanged(); // Trigger autosave
  };

  const updateKeyEntry = (index: number, field: keyof KeyEntry, value: string) => {
    const newKeys = [...keysData];
    newKeys[index] = { ...newKeys[index], [field]: value };
    setKeysData(newKeys);
    autosave.markAsChanged(); // Trigger autosave
  };

  const removeKeyEntry = (index: number) => {
    setKeysData(prevKeys => prevKeys.filter((_, i) => i !== index));
    autosave.markAsChanged(); // Trigger autosave
  };

  const updateKeyPhotos = (index: number, photos: RoomPhoto[]) => {
    const newKeys = [...keysData];
    newKeys[index] = { ...newKeys[index], photos };
    setKeysData(newKeys);
    autosave.markAsChanged();
  };

  const deleteKeyPhoto = (keyIndex: number, photoId: string) => {
    const newKeys = [...keysData];
    const currentPhotos = newKeys[keyIndex].photos || [];
    newKeys[keyIndex] = { 
      ...newKeys[keyIndex], 
      photos: currentPhotos.filter(p => p.id !== photoId) 
    };
    setKeysData(newKeys);
    autosave.markAsChanged();
  };

  // --- Handlers for Certificates and Meter Readings ---
  const handleCertificateChange = (
    certType: 'gas' | 'electrical',
    field: 'date' | 'number',
    value: string
  ) => {
    setCertificates(prev => ({
      ...prev,
      [certType]: {
        ...prev[certType],
        [field]: value,
      },
    }));
    autosave.markAsChanged(); // Trigger autosave
  };

  const updateCertificatePhotos = (certType: 'gas' | 'electrical', photos: RoomPhoto[]) => {
    setCertificates(prev => ({
      ...prev,
      [certType]: {
        ...prev[certType],
        photos,
      },
    }));
    autosave.markAsChanged();
  };

  const deleteCertificatePhoto = (certType: 'gas' | 'electrical', photoId: string) => {
    setCertificates(prev => ({
      ...prev,
      [certType]: {
        ...prev[certType],
        photos: (prev[certType].photos || []).filter((p: any) => p.id !== photoId),
      },
    }));
    autosave.markAsChanged();
  };

  const handleMeterReadingChange = (
    meterType: 'gas' | 'electricity' | 'water',
    field: 'reading' | 'location' | 'supplier' | 'serialNo',
    value: string
  ) => {
    setMeterReadings(prev => ({
      ...prev,
      [meterType]: {
        ...prev[meterType],
        [field]: value,
      },
    }));
    autosave.markAsChanged(); // Trigger autosave
  };

  const updateMeterPhotos = (meterType: 'gas' | 'electricity' | 'water', photos: RoomPhoto[]) => {
    setMeterReadings(prev => ({
      ...prev,
      [meterType]: {
        ...prev[meterType],
        photos,
      },
    }));
    autosave.markAsChanged();
  };

  const deleteMeterPhoto = (meterType: 'gas' | 'electricity' | 'water', photoId: string) => {
    setMeterReadings(prev => ({
      ...prev,
      [meterType]: {
        ...prev[meterType],
        photos: (prev[meterType].photos || []).filter((p: any) => p.id !== photoId),
      },
    }));
    autosave.markAsChanged();
  };

  // State for section photo capture with ContinuousCamera
  const [isSectionCameraOpen, setIsSectionCameraOpen] = useState(false);
  const [sectionCameraContext, setSectionCameraContext] = useState<{
    updateFn: (photos: RoomPhoto[]) => void;
    currentPhotos: RoomPhoto[];
  } | null>(null);

  // Generic photo capture handler for sections (keys, certificates, meters)
  const handleSectionPhotoCapture = (
    updateFn: (photos: RoomPhoto[]) => void,
    currentPhotos: RoomPhoto[]
  ) => {
    // Store the update function and open ContinuousCamera
    setSectionCameraContext({ updateFn, currentPhotos });
    setIsSectionCameraOpen(true);
  };

  // Handle photos captured from section ContinuousCamera
  const handleSectionPhotosCaptured = async (photoDataUrls: string[]) => {
    if (!sectionCameraContext) return;

    const { updateFn, currentPhotos } = sectionCameraContext;
    
    try {
      const cameraService = CameraService.getInstance();
      // Process and optimize photos from custom camera
      const photos = await cameraService.processCustomCameraPhotos(photoDataUrls);
      
      // Process each captured photo
      for (const photoData of photos) {
        if (photoData && photoData.base64String) {
          const dataUrl = `data:image/${photoData.format};base64,${photoData.base64String}`;
          const newPhoto: RoomPhoto = {
            id: generateUUID(),
            fileName: `${currentInventoryId || 'inventory'}_section_${Date.now()}.jpg`,
            localPath: dataUrl,
            url: '',
            uploading: true,
          };
          
          const updatedPhotos = [...currentPhotos, newPhoto];
          updateFn(updatedPhotos);
          
          // Upload to Supabase
          try {
            const publicUrl = await uploadPhotoToSupabase(
              dataUrl,
              newPhoto.fileName,
              'inventory-photos'
            );
            
            // Update with URL
            const finalPhotos = updatedPhotos.map(p =>
              p.id === newPhoto.id ? { ...p, url: publicUrl, uploading: false } : p
            );
            updateFn(finalPhotos);
          } catch (uploadError) {
            console.error('Upload failed:', uploadError);
            const errorPhotos = updatedPhotos.map(p =>
              p.id === newPhoto.id ? { ...p, uploading: false, error: 'Upload failed' } : p
            );
            updateFn(errorPhotos);
          }
        }
      }
    } catch (error) {
      console.error('Photo capture failed:', error);
    }
  };

  // Handle section camera close
  const handleSectionCameraClose = () => {
    setIsSectionCameraOpen(false);
    setSectionCameraContext(null);
  };

  // Generic photo upload handler for sections
  const handleSectionPhotoUpload = async (
    updateFn: (photos: RoomPhoto[]) => void,
    currentPhotos: RoomPhoto[],
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newPhotos: RoomPhoto[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      
      await new Promise((resolve) => {
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          const newPhoto: RoomPhoto = {
            id: generateUUID(),
            fileName: `${currentInventoryId || 'inventory'}_section_${Date.now()}_${i}.jpg`,
            localPath: dataUrl,
            url: '',
            uploading: true,
          };
          newPhotos.push(newPhoto);
          resolve(null);
        };
        reader.readAsDataURL(file);
      });
    }
    
    const updatedPhotos = [...currentPhotos, ...newPhotos];
    updateFn(updatedPhotos);
    
    // Upload all photos
    for (const photo of newPhotos) {
      try {
        const compressedImage = await compressImage(photo.localPath || '', 1600, 0.9);
        const publicUrl = await uploadPhotoToSupabase(
          compressedImage,
          photo.fileName,
          'inventory-photos'
        );
        
        const finalPhotos = updatedPhotos.map(p =>
          p.id === photo.id ? { ...p, url: publicUrl, uploading: false } : p
        );
        updateFn(finalPhotos);
      } catch (uploadError) {
        console.error('Error uploading section photo:', uploadError);
        const errorPhotos = updatedPhotos.map(p =>
          p.id === photo.id ? { ...p, uploading: false, error: 'Upload failed' } : p
        );
        updateFn(errorPhotos);
      }
    }
    
    // Clear file input
    event.target.value = '';
  };

  // --- Restore useEffects (Example: the debounced effect for property address) ---
  // useDebouncedEffect(() => {
  //   console.log('Debounced property address (from restored effect):', propertyDetails.address);
  //   logEvent('propertyAddressChangeDebounced', { address: propertyDetails.address }, {});
  // }, 500, [propertyDetails.address, logEvent]);

  useEffect(() => {
    console.log('Property address changed (no debounce):', propertyDetails.address);
    logEvent('propertyAddressChange', { address: propertyDetails.address }, {}); 
  }, [propertyDetails.address, logEvent]); // logEvent can now be safely included

  useEffect(() => {
    // Example of a simple effect - This one syncs inputs, might be too complex for now if it fails
    // For now, let's keep it simple or comment out if it causes issues.
    // const syncInputs = () => { /* ... */ };
    // const timer = setTimeout(syncInputs, 500);
    // return () => clearTimeout(timer);
    console.log("A simple useEffect ran.");
  }, []);


  // Effect for body scroll lock for final signature modal
  useEffect(() => {
    if (isFinalSignatureModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFinalSignatureModalOpen]);

  // Effect for body scroll lock during photo uploads
  useEffect(() => {
    if (isUploadingPhotos) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      document.body.style.right = '0';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.left = '';
      document.body.style.right = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.left = '';
      document.body.style.right = '';
    };
  }, [isUploadingPhotos]);

  // Restore actual handlers for PropertyDetailsForm
  const handleAddTenant = () => {
    setPropertyDetails(prev => ({
        ...prev,
        tenantNames: [...prev.tenantNames, ''] 
    }));
  };

  const handleRemoveTenant = (index: number) => {
     setPropertyDetails(prev => {
        const newTenants = prev.tenantNames.slice(); 
        newTenants.splice(index, 1); 
        return { ...prev, tenantNames: newTenants };
    });
  };
  
  const handleTenantNameChange = (index: number, value: string) => {
    const newTenantNames = [...propertyDetails.tenantNames];
    newTenantNames[index] = value;
    updatePropertyDetail('tenantNames', newTenantNames);
  };

  // --- Signature Handlers ---
  const openSignatureModal = (type: 'tenant' | 'landlord' | 'assessor' | 'customer', roomIdx?: number) => {
    setCurrentSignatureType(type);
    if (roomIdx !== undefined) {
      setCurrentRoomIndexForSig(roomIdx); // For room-specific signatures
    }
    setShowSignatureModal(true);
  };

  const handleSaveSignature = () => {
    let sigRef: React.RefObject<SignatureCanvas> | undefined;
    if (currentSignatureType === 'assessor') sigRef = assessorSignatureRef;
    else if (currentSignatureType === 'customer') sigRef = customerSignatureRef;
    // For room-specific, you might need a different ref or logic
    else if (currentSignatureType === 'tenant') sigRef = tenantSignatureRef; // Example
    else if (currentSignatureType === 'landlord') sigRef = landlordSignatureRef; // Example

    if (sigRef?.current && !sigRef.current.isEmpty()) {
      const dataURL = sigRef.current.toDataURL();
      const currentDate = new Date().toLocaleDateString('en-GB');
      
      if (currentSignatureType === 'assessor' || currentSignatureType === 'customer') {
        const dateField = currentSignatureType === 'assessor' ? 'assessorDate' : 'customerDate';
        setSignatures(prev => ({ 
          ...prev, 
          [currentSignatureType]: dataURL,
          [dateField]: currentDate
        }));
      } else if (rooms[currentRoomIndexForSig]) {
        // Handle room-specific signatures (example, adjust to your data model)
        setRooms(prevRooms => {
          const newRooms = [...prevRooms];
          if (!newRooms[currentRoomIndexForSig].signatures) {
            newRooms[currentRoomIndexForSig].signatures = [];
          }
          // Remove old signature of this type for this room and add new one
          newRooms[currentRoomIndexForSig].signatures = newRooms[currentRoomIndexForSig].signatures!.filter(
            (sig) => sig.type !== currentSignatureType
          );
          newRooms[currentRoomIndexForSig].signatures!.push({ 
            type: currentSignatureType, 
            signature: dataURL,
            date: new Date().toISOString().split('T')[0]
          });
          return newRooms;
        });
      }
      autosave.markAsChanged(); // Trigger autosave for signatures
      logEvent('signatureSaved', { type: currentSignatureType, room: currentSignatureType === 'tenant' || currentSignatureType === 'landlord' ? currentRoomIndexForSig : undefined }, {});
    }
    setShowSignatureModal(false);
    sigRef?.current?.clear();
  };
  
  const handleClearSignature = () => {
    let sigRef: React.RefObject<SignatureCanvas> | undefined;
    if (currentSignatureType === 'assessor') sigRef = assessorSignatureRef;
    else if (currentSignatureType === 'customer') sigRef = customerSignatureRef;
    else if (currentSignatureType === 'tenant') sigRef = tenantSignatureRef;
    else if (currentSignatureType === 'landlord') sigRef = landlordSignatureRef;
    
    sigRef?.current?.clear();
  };

  const openFinalSignatureModal = () => setIsFinalSignatureModalOpen(true);
  const closeFinalSignatureModal = () => setIsFinalSignatureModalOpen(false);

  const handleSaveFinalSignature = () => {
    // Assuming final signature is for assessor for now, adjust as needed
    if (finalSignatureModalRef.current && !finalSignatureModalRef.current.isEmpty()) {
      const dataURL = finalSignatureModalRef.current.toDataURL();
      setSignatures(prev => ({ ...prev, assessor: dataURL })); // Example: saving to assessor
      logEvent('finalSignatureSaved', { type: 'assessor' }, {});
    }
    closeFinalSignatureModal();
    finalSignatureModalRef.current?.clear();
  };

  const handleClearFinalSignature = () => {
    finalSignatureModalRef.current?.clear();
  };

  // --- Restore Room and Item Handlers ---
  const selectRoom = (roomIndex: number) => {
      setActiveRoom(roomIndex);
  };

  const addNewRoom = () => {
    const newRoomName = `Room ${rooms.length + 1}`; // Basic default name
    // Add a default set of items for a new room if desired, or start empty
    const defaultNewRoomItems: InventoryItem[] = [
      // Start with one empty item as a placeholder
      { category: '', condition: '', notes: '', photos: [] }
    ];
    setRooms([...rooms, { 
      name: newRoomName, 
      items: defaultNewRoomItems,
      rating: undefined,
      cleanliness: ''
    }]);
    setActiveRoom(rooms.length);
    autosave.markAsChanged();
  };

  const updateRoomName = (roomIndex: number, newName: string) => {
    setRooms(prevRooms => {
      const newRooms = [...prevRooms];
      newRooms[roomIndex] = { ...newRooms[roomIndex], name: newName };
      return newRooms;
      });
    autosave.markAsChanged(); // Trigger autosave
    };
    
  const removeItemFromRoom = (roomIndex: number, itemIndex: number) => {
    setRooms(prevRooms => {
      const newRooms = JSON.parse(JSON.stringify(prevRooms));
      newRooms[roomIndex].items.splice(itemIndex, 1);
      return newRooms;
    });
    autosave.markAsChanged(); // Trigger autosave
  };

  const removeRoom = (roomIndex: number) => {
    if (!window.confirm(`Are you sure you want to delete the room "${rooms[roomIndex]?.name || `Room ${roomIndex + 1}`}" and all its items?`)) {
      return;
    }
    setRooms(prevRooms => {
      const newRooms = prevRooms.filter((_, index) => index !== roomIndex);
      // Adjust active room index if needed
      if (activeRoom === roomIndex) {
        setActiveRoom(Math.max(0, roomIndex - 1));
      } else if (activeRoom > roomIndex) {
        setActiveRoom(activeRoom - 1);
      }
      return newRooms;
    });
    autosave.markAsChanged(); // Trigger autosave
  };

  const addItemToRoom = (roomIndex: number) => {
    setRooms(prevRooms => {
      const newRooms = JSON.parse(JSON.stringify(prevRooms));
      newRooms[roomIndex].items.push({
        category: "New Item", // Default category
        description: "",
        condition: "Good", // Default condition
        notes: "",
        photos: []
      });
      return newRooms;
      });
    autosave.markAsChanged(); // Trigger autosave
    };
    
  const addItemAfter = (roomIndex: number, itemIndex: number) => {
    setRooms(prevRooms => {
      const newRooms = JSON.parse(JSON.stringify(prevRooms)); 
      const newItem: InventoryItem = {
        category: "", 
        condition: "Good", 
        notes: "",
        photos: []
      };
      newRooms[roomIndex].items.splice(itemIndex + 1, 0, newItem);
      return newRooms;
    });
    autosave.markAsChanged(); // Trigger autosave
  };

  // Initialize camera service
  const cameraService = CameraService.getInstance();
  
  // Placeholder for photo handlers needed by RoomInventoryItem
  const placeholderPhotoHandler = () => { console.warn("Photo handler not implemented yet."); return <button disabled>📷</button>; };

  // --- Restore actual photo rendering functions ---
  const renderRoomCameraButton = (roomIndex: number, itemIndex: number) => {
    // Determine if custom camera is supported
    const supportsCustomCamera = cameraService.supportsCustomCamera();
    
    const handlePhotosCaptured = async (photos: PhotoData[]) => {
      console.log(`[PropertyInventory] 📸 Photos captured for R${roomIndex}I${itemIndex}:`, photos.length);
      
      // If no photos captured (user cancelled), don't show modal
      if (!photos || photos.length === 0) {
        console.log('[PropertyInventory] No photos captured, skipping');
        return;
      }
      
      // Show loading modal IMMEDIATELY when photos are captured
      setIsUploadingPhotos(true);
      console.log('[PropertyInventory] Photos captured - showing loading modal immediately');
      
      // Create photo metadata WITHOUT url (will be uploaded)
      const newPhotos: RoomPhoto[] = photos.map(photo => ({
        id: `photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        fileName: `photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.jpg`,
        localPath: `data:image/${photo.format};base64,${photo.base64String}`,
        url: undefined, // ✅ Will be set after upload
        uploading: true, // ✅ Mark as uploading
        timestamp: new Date().toISOString(),
        processed: false,
        retries: 0,
        metadata: {
          originalSize: 0,
          compressedSize: 0,
        }
      }));
      
      // Add photos to state immediately (with uploading flag)
      const currentPhotos = rooms[roomIndex].items[itemIndex].photos || [];
      const updatedPhotos = [...currentPhotos, ...newPhotos];
      updateRoomItem(roomIndex, itemIndex, { photos: updatedPhotos });
      
      // ✅ IMMEDIATE UPLOAD: Upload each photo to Supabase Storage
      let successCount = 0;
      let failCount = 0;
      
      for (const photo of newPhotos) {
        try {
          console.log(`[PropertyInventory] 📤 Uploading photo: ${photo.fileName}`);
          
          // Upload to Supabase Storage
          const supabaseUrl = await uploadPhotoToSupabase(
            photo.localPath!,
            photo.fileName,
            'inventory-photos'
          );
          
          // ✅ Use callback-based update to avoid stale closure state
          setRooms(prevRooms => {
            const newRooms = [...prevRooms];
            const targetRoom = newRooms[roomIndex];
            if (!targetRoom) return prevRooms;
            
            const targetItem = targetRoom.items[itemIndex];
            if (!targetItem) return prevRooms;
            
            // Update the specific photo with URL
            const updatedPhotos = targetItem.photos.map(p => 
              p.id === photo.id 
                ? { ...p, url: supabaseUrl, uploading: false, error: undefined, localPath: undefined }
                : p
            );
            
            // Create new items array with updated photos
            const newItems = [...targetRoom.items];
            newItems[itemIndex] = { ...targetItem, photos: updatedPhotos };
            
            // Create new room with updated items
            newRooms[roomIndex] = { ...targetRoom, items: newItems };
            
            return newRooms;
          });
          
          successCount++;
          console.log(`[PropertyInventory] ✅ Photo uploaded (${successCount}/${newPhotos.length}): ${photo.fileName}`);
        } catch (error) {
          console.error(`[PropertyInventory] ❌ Upload failed: ${photo.fileName}`, error);
          failCount++;
          
          // ✅ Use callback-based update for error handling too
          setRooms(prevRooms => {
            const newRooms = [...prevRooms];
            const targetRoom = newRooms[roomIndex];
            if (!targetRoom) return prevRooms;
            
            const targetItem = targetRoom.items[itemIndex];
            if (!targetItem) return prevRooms;
            
            // Mark photo with error (keep localPath for recovery)
            const updatedPhotos = targetItem.photos.map(p => 
              p.id === photo.id 
                ? { ...p, uploading: false, error: `Upload failed: ${(error as Error).message}` }
                : p
            );
            
            // Create new items array with updated photos
            const newItems = [...targetRoom.items];
            newItems[itemIndex] = { ...targetItem, photos: updatedPhotos };
            
            // Create new room with updated items
            newRooms[roomIndex] = { ...targetRoom, items: newItems };
            
            return newRooms;
          });
        }
      }
      
      // ✅ FORCE IMMEDIATE SAVE: Trigger immediate autosave after uploads complete
      console.log(`[PropertyInventory] 💾 Upload batch complete: ${successCount} succeeded, ${failCount} failed - triggering IMMEDIATE save`);
      
      // Trigger immediate autosave
      autosave.triggerManualSave();
      
      // ✅ CRITICAL: Stop processing to hide upload modal
      stopProcessing(roomIndex, itemIndex);
      console.log(`[PropertyInventory] ✅ Stopped processing for R${roomIndex}I${itemIndex} - modal should hide`);
    };
    
    if (supportsCustomCamera) {
      return (
        <PhotoCapture
          onPhotosCaptured={handlePhotosCaptured}
          maxPhotos={100}
          buttonLabel="Camera"
          allowUpload={false}
        />
      );
    } else {
      // Fall back to native camera
      const handler = () => {
        console.log(`[DEBUG] Camera button handler triggered for R${roomIndex}I${itemIndex}`);
        
        // Show loading modal IMMEDIATELY when button is clicked
        setIsUploadingPhotos(true);
        console.log('[PropertyInventory] Camera button clicked - showing loading modal immediately');
        
        // Safeguard: Hide modal after timeout if no processing starts (user cancelled)
        // iOS photo capture can take 10-20 seconds, so use longer timeout for mobile
        const cancelTimeoutDuration = isMobileDevice() ? 30000 : 5000;
        const cancelTimeout = setTimeout(() => {
          if (processingItems.size === 0) {
            console.log(`[PropertyInventory] No processing detected after ${cancelTimeoutDuration/1000}s, hiding modal (user likely cancelled)`);
            setIsUploadingPhotos(false);
          }
        }, cancelTimeoutDuration);
        
        // Small delay to ensure modal renders before heavy processing
        setTimeout(() => {
          if (isMobileDevice()) {
            console.log(`[DEBUG] Using Capacitor Camera API for mobile device`);
            handleCameraCapture(roomIndex, itemIndex);
          } else {
            console.log(`[DEBUG] Using file input for desktop`);
            handleRoomPhotoUpload(roomIndex, itemIndex, undefined);
          }
          
          // If processing starts, clear the cancel timeout
          setTimeout(() => {
            if (processingItems.size > 0) {
              clearTimeout(cancelTimeout);
            }
          }, 100);
        }, 50); // 50ms delay to let modal render
      };
      
      return renderRoomCameraButtonUtil(
        roomIndex, 
        itemIndex, 
        handler,
        isProcessing(roomIndex, itemIndex)
      ); 
    }
  };

  const renderRoomUploadButton = (roomIndex: number, itemIndex: number) => {
     const handler = () => {
       console.log(`[DEBUG] Upload button handler triggered for R${roomIndex}I${itemIndex}`);
       
       // Show loading modal IMMEDIATELY when button is clicked
       setIsUploadingPhotos(true);
       console.log('[PropertyInventory] Upload button clicked - showing loading modal immediately');
       
       // Safeguard: Hide modal after timeout if no processing starts (user cancelled)
       // iOS photo selection can take 10-20 seconds, so use longer timeout for mobile
       const cancelTimeoutDuration = isMobileDevice() ? 30000 : 5000;
       const cancelTimeout = setTimeout(() => {
         if (processingItems.size === 0) {
           console.log(`[PropertyInventory] No processing detected after ${cancelTimeoutDuration/1000}s, hiding modal (user likely cancelled)`);
           setIsUploadingPhotos(false);
         }
       }, cancelTimeoutDuration);
       
       // Small delay to ensure modal renders before heavy processing
       setTimeout(() => {
         if (isMobileDevice()) {
           console.log(`[DEBUG] Using Capacitor Gallery/Photos API for mobile device`);
           handleGallerySelect(roomIndex, itemIndex);
         } else {
           console.log(`[DEBUG] Using file input for desktop`);
           handleRoomPhotoUpload(roomIndex, itemIndex, undefined);
         }
         
         // If processing starts, clear the cancel timeout
         setTimeout(() => {
           if (processingItems.size > 0) {
             clearTimeout(cancelTimeout);
           }
         }, 100);
       }, 50); // 50ms delay to let modal render
     };
     
     return renderRoomUploadButtonUtil(handler, isProcessing(roomIndex, itemIndex)); 
  };
    
  const handleSaveInventory = async () => {
    setIsSaving(true);
    logEvent('inventorySaveAttempt', { currentInventoryId }, {});

    try {
      await autosave.triggerManualSave();
      alert(`Inventory "${propertyDetails.address}" saved successfully to cloud!`);
      logEvent('inventorySaveSuccess', { currentInventoryId }, {});
    } catch (error) {
      console.error("Error saving inventory:", error);
      logEvent('inventorySaveError', { name: currentInventoryId, error: (error as Error).message }, {}, (error as Error));
      alert(`Failed to save inventory to cloud: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ErrorBoundary>
      {/* Photo Upload Loading Modal */}
      <InventoryLoadingModal 
        isOpen={isUploadingPhotos}
        mode="photos"
      />
      
      <div
        ref={containerRef}
        className={styles.darkContainer}
        style={{
          paddingTop: 'max(34px, env(safe-area-inset-top))',
          paddingLeft: 'max(18px, env(safe-area-inset-left))',
          paddingRight: 'max(18px, env(safe-area-inset-right))',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))'
        }}
      >
        {/* Address Input Modal */}
        <AddressInputModal
          isOpen={showAddressModal}
          onAddressSubmit={handleAddressSubmit}
          onSkip={handleSkipAddress}
          title="Create Property Inventory"
          description="Enter the property address to get started with auto-save enabled."
          allowSkip={true}
          initialAddress={propertyDetails.address}
        />

        <header className="bg-gradient-to-r from-orange-600 via-orange-500 to-amber-600 shadow-2xl sticky top-0 z-40">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/0 via-white/10 to-amber-600/0"></div>
          <div className="relative w-full px-4 sm:px-6 flex items-center justify-between app-header-padding">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                  Property Inventory
                </h1>
                <p className="text-orange-100 text-xs sm:text-sm hidden sm:block">Create clean reports with consistent data entry</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3">
              <span style={{
                padding: '8px 14px',
                borderRadius: '999px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(8px)',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: '600',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                whiteSpace: 'nowrap'
              }}>
                {reportType === 'checkIn' ? 'Check-in' : reportType === 'midTerm' ? 'Mid-Term' : 'Check-out'}
              </span>
              
              <button 
                onClick={handleSaveInventory} 
                type="button" 
                disabled={isSaving}
                className="hidden sm:flex items-center px-3 sm:px-4 py-2 rounded-xl text-white/90 hover:text-white bg-white/20 hover:bg-white/30 transition-all duration-200 backdrop-blur-sm border border-white/30 hover:border-white/40 shadow-lg hover:shadow-xl text-xs sm:text-sm font-semibold"
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
              
              <button 
                onClick={handleExportClick} 
                type="button"
                className="hidden sm:flex items-center px-3 sm:px-4 py-2 rounded-xl text-white bg-white/30 hover:bg-white/40 transition-all duration-200 backdrop-blur-sm border border-white/40 hover:border-white/50 shadow-lg hover:shadow-xl text-xs sm:text-sm font-bold"
              >
                Export
              </button>
              
              <button
                onClick={() => onNavigate({ page: 'start' })}
                title="Go to Home"
                className="p-2 sm:p-2.5 rounded-xl text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
              >
                <Home size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>
          </div>
        </header>

        <main style={{ maxWidth: '1060px', margin: '0 auto' }}>
          {/* Report Type Selector */}
          <div className={styles.card}>
            <div className={styles.cardHeader} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <div className={styles.badge + ' ' + styles.badgeYellow}>📄</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h2 style={{ margin: 0, fontSize: '15px', letterSpacing: '-0.01em' }}>Report Type</h2>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--pi-muted)', lineHeight: '1.3' }}>Choose the flow for check-in, mid-term inspection, or check-out.</p>
                </div>
              </div>
              <div className={styles.segmented} role="tablist" aria-label="Report Type" style={{ width: '100%' }}>
                <input id="rt-in" name="reportType" type="radio" checked={reportType === 'checkIn'} onChange={() => setReportType('checkIn')} />
                <label htmlFor="rt-in">Check-in</label>
                <input id="rt-mid" name="reportType" type="radio" checked={reportType === 'midTerm'} onChange={() => setReportType('midTerm')} />
                <label htmlFor="rt-mid">Mid-Term</label>
                <input id="rt-out" name="reportType" type="radio" checked={reportType === 'checkOut'} onChange={() => setReportType('checkOut')} />
                <label htmlFor="rt-out">Check-out</label>
              </div>
            </div>
            <div className={styles.cardBody}>
              {/* Basic Information */}
              <div className={styles.card} style={{ boxShadow: 'none', borderRadius: '18px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className={styles.cardHeader} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 16px 12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                    <div className={styles.badge + ' ' + styles.badgeBlue}>🧾</div>
                    <div style={{ minWidth: 0 }}>
                      <h2 style={{ margin: 0, fontSize: '16px' }}>Basic Information</h2>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pi-muted)' }}>Core property and inspection context.</p>
                    </div>
                  </div>
                </div>
                <div className={styles.cardBody} style={{ padding: '16px' }}>
                  <div className={styles.section}>
                    <div>
                      <div className={styles.label}>
                        <div><strong>Property Address</strong><span className={styles.required}>*</span></div>
                      </div>
                      <input 
                        className={styles.input} 
                        value={propertyDetails.address} 
                        onChange={(e) => updatePropertyDetail('address', e.target.value)}
                        placeholder="Enter property address"
                      />
                      <div className={styles.help}>Tip: allow postcode search + quick-select recent addresses.</div>
                    </div>
                    <div className={styles.row + ' ' + styles.cols3}>
                      <div>
                        <div className={styles.label}><strong>Property Type</strong></div>
                        <select 
                          className={styles.select} 
                          value={propertyDetails.propertyType}
                          onChange={(e) => updatePropertyDetail('propertyType', e.target.value)}
                        >
                          <option value="">Select an option</option>
                          <option value="flat">Flat</option>
                          <option value="house">House</option>
                          <option value="maisonette">Maisonette</option>
                          <option value="bungalow">Bungalow</option>
                          <option value="studio">Studio</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <div className={styles.label}><strong>Bedrooms</strong></div>
                        <select 
                          className={styles.select}
                          value={propertyDetails.bedrooms}
                          onChange={(e) => updatePropertyDetail('bedrooms', e.target.value)}
                        >
                          <option value="">Select an option</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5+">5+</option>
                        </select>
                      </div>
                      <div>
                        <div className={styles.label}><strong>Cleanliness Standard</strong></div>
                        <select 
                          className={styles.select}
                          value={propertyDetails.cleanliness}
                          onChange={(e) => updatePropertyDetail('cleanliness', e.target.value)}
                        >
                          <option value="">Select an option</option>
                          <option value="professionally cleaned">Professionally Cleaned</option>
                          <option value="good domestic standard">Good Domestic</option>
                          <option value="average domestic standard">Average Domestic</option>
                          <option value="poor domestic standard">Poor Domestic</option>
                          <option value="not cleaned">Not Cleaned</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <div className={styles.label}><strong>Additional Property Description</strong></div>
                      <textarea 
                        className={styles.textarea} 
                        value={propertyDetails.propertyDescription}
                        onChange={(e) => updatePropertyDetail('propertyDescription', e.target.value)}
                        placeholder="Add access notes, meter locations, parking, concierge details…"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Important Dates */}
              <div className={styles.card} style={{ marginTop: '16px', boxShadow: 'none', borderRadius: '18px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className={styles.cardHeader} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 16px 12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                    <div className={styles.badge + ' ' + styles.badgeGreen}>📅</div>
                    <div style={{ minWidth: 0 }}>
                      <h2 style={{ margin: 0, fontSize: '16px' }}>Important Dates</h2>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pi-muted)' }}>Schedule and reference dates.</p>
                    </div>
                  </div>
                </div>
                <div className={styles.cardBody} style={{ padding: '16px' }}>
                  <div className={styles.row + ' ' + styles.cols2}>
                    <div>
                      <div className={styles.label}><strong>Inspection Date</strong></div>
                      <input 
                        type="date"
                        className={styles.input} 
                        value={propertyDetails.inspectionDate}
                        onChange={(e) => updatePropertyDetail('inspectionDate', e.target.value)}
                      />
                    </div>
                    <div>
                      <div className={styles.label}><strong>{reportType === 'checkIn' ? 'Check-in' : reportType === 'midTerm' ? 'Inspection' : 'Check-out'} Date</strong></div>
                      <input 
                        type="date"
                        className={styles.input} 
                        value={reportType === 'checkIn' ? propertyDetails.checkInDate : reportType === 'midTerm' ? propertyDetails.checkInDate : propertyDetails.checkOutDate}
                        onChange={(e) => updatePropertyDetail(reportType === 'checkIn' ? 'checkInDate' : reportType === 'midTerm' ? 'checkInDate' : 'checkOutDate', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* People Involved */}
              <div className={styles.card} style={{ marginTop: '16px', boxShadow: 'none', borderRadius: '18px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className={styles.cardHeader} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 16px 12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                    <div className={styles.badge + ' ' + styles.badgePurple}>👥</div>
                    <div style={{ minWidth: 0 }}>
                      <h2 style={{ margin: 0, fontSize: '16px' }}>People Involved</h2>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pi-muted)' }}>Who's responsible and who to contact.</p>
                    </div>
                  </div>
                </div>
                <div className={styles.cardBody} style={{ padding: '16px' }}>
                  <div className={styles.section}>
                    <div className={styles.row + ' ' + styles.cols2}>
                      <div>
                        <div className={styles.label}><strong>Inspected By</strong></div>
                        <input 
                          className={styles.input} 
                          value={propertyDetails.inspectedBy}
                          onChange={(e) => updatePropertyDetail('inspectedBy', e.target.value)}
                          placeholder="Inspector name"
                        />
                      </div>
                      <div>
                        <div className={styles.label}><strong>Landlord Name</strong></div>
                        <input 
                          className={styles.input} 
                          value={propertyDetails.landlordName}
                          onChange={(e) => updatePropertyDetail('landlordName', e.target.value)}
                          placeholder="Landlord / agency"
                        />
                      </div>
                    </div>
                    {propertyDetails.tenantNames && propertyDetails.tenantNames.length > 0 ? (
                      <div>
                        {propertyDetails.tenantNames.map((tenant, index) => (
                          <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input 
                              className={styles.input} 
                              value={tenant}
                              onChange={(e) => handleTenantNameChange(index, e.target.value)}
                              placeholder="Tenant name"
                            />
                            <button 
                              className={styles.btn}
                              onClick={() => handleRemoveTenant(index)}
                              type="button"
                              style={{ padding: '8px 12px' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyState}>
                        <div>
                          <div className={styles.emptyText}>Tenants</div>
                          <div className={styles.emptyDesc}>Add tenants for signature, contact details, and occupancy notes.</div>
                        </div>
                        <button 
                          onClick={handleAddTenant} 
                          type="button"
                          style={{
                            padding: '10px 20px',
                            borderRadius: '999px',
                            border: 'none',
                            background: 'linear-gradient(135deg, var(--pi-accent), #f97316)',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontWeight: '650',
                            fontSize: '14px',
                            transition: 'all 0.18s ease',
                            boxShadow: '0 6px 16px rgba(245, 158, 11, 0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Plus size={16} /> Add Tenant
                        </button>
                      </div>
                    )}
                    {propertyDetails.tenantNames && propertyDetails.tenantNames.length > 0 && (
                      <button 
                        onClick={handleAddTenant} 
                        type="button" 
                        style={{ 
                          marginTop: '8px',
                          padding: '8px 16px',
                          borderRadius: '999px',
                          border: '1px solid var(--pi-stroke-2)',
                          background: '#ffffff',
                          color: 'var(--pi-text)',
                          cursor: 'pointer',
                          fontWeight: '650',
                          fontSize: '13px',
                          transition: 'all 0.18s ease',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Plus size={14} /> Add Another Tenant
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rooms Section */}
          <div style={{ marginTop: '18px' }}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <div className={styles.badge + ' ' + styles.badgeBlue}>🏠</div>
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ margin: 0, fontSize: '16px' }}>Room Inventory</h2>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pi-muted)' }}>Document condition of each room.</p>
                  </div>
                </div>
                <button className={styles.btn} onClick={addNewRoom} type="button">
                  <Plus size={16} style={{ marginRight: '4px' }} /> Add Room
                </button>
              </div>
              <div className={styles.cardBody}>
                {/* Room Tabs */}
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', marginBottom: '16px', padding: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.10)' }}>
                  {rooms.map((room, index) => (
                    <button
                      key={room.name + index}
                      onClick={() => selectRoom(index)}
                      className={activeRoom === index ? styles.btn + ' ' + styles.btnPrimary : styles.btn}
                      style={{ 
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px'
                      }}
                      type="button"
                    >
                      {room.name || `Room ${index + 1}`}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeRoom(index); }}
                        style={{ 
                          marginLeft: '4px',
                          padding: '2px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: activeRoom === index ? '#1a1200' : 'var(--pi-muted)',
                          opacity: 0.7
                        }}
                        aria-label={`Delete ${room.name || `Room ${index + 1}`}`}
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    </button>
                  ))}
                </div>
                
                {/* Active Room Content */}
                {rooms[activeRoom] && (
                  <div key={activeRoom} style={{ marginTop: '16px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <div className={styles.label}><strong>Room Name</strong></div>
                      <input
                        type="text"
                        className={styles.input}
                        value={rooms[activeRoom].name}
                        onChange={(e) => updateRoomName(activeRoom, e.target.value)}
                        placeholder="e.g., Living Room, Bedroom 1"
                        style={{ fontSize: '16px', fontWeight: '600' }}
                      />
                    </div>

                {/* Mid-Term Report: Simplified UI with rating, cleanliness, photos and notes */}
                {reportType === 'midTerm' ? (
                  <div style={{ marginTop: '20px' }}>
                    {/* Room Rating */}
                    <div style={{ marginBottom: '20px' }}>
                      <div className={styles.label} style={{ marginBottom: '12px' }}>
                        <strong>Room Condition Rating</strong>
                        <span style={{ fontSize: '12px', color: 'var(--pi-muted)', marginLeft: '8px' }}>
                          (1 = Excellent, 5 = Poor)
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => {
                              const updatedRooms = [...rooms];
                              updatedRooms[activeRoom] = {
                                ...updatedRooms[activeRoom],
                                rating: rating as 1 | 2 | 3 | 4 | 5
                              };
                              setRooms(updatedRooms);
                            }}
                            style={{
                              padding: '12px 20px',
                              fontSize: '16px',
                              fontWeight: '600',
                              border: rooms[activeRoom].rating === rating ? '2px solid #009688' : '2px solid #e0e0e0',
                              borderRadius: '8px',
                              background: rooms[activeRoom].rating === rating ? '#e0f2f1' : 'white',
                              color: rooms[activeRoom].rating === rating ? '#009688' : '#666',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              minWidth: '50px'
                            }}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                      {rooms[activeRoom].rating && (
                        <div style={{ 
                          marginTop: '10px', 
                          fontSize: '13px', 
                          color: '#009688',
                          fontWeight: '500'
                        }}>
                          {rooms[activeRoom].rating === 1 && '⭐ Excellent condition'}
                          {rooms[activeRoom].rating === 2 && '✨ Good condition'}
                          {rooms[activeRoom].rating === 3 && '👍 Average condition'}
                          {rooms[activeRoom].rating === 4 && '⚠️ Below average condition'}
                          {rooms[activeRoom].rating === 5 && '❌ Poor condition'}
                        </div>
                      )}
                    </div>

                    {/* Room Cleanliness */}
                    <div style={{ marginBottom: '20px' }}>
                      <div className={styles.label}><strong>Cleanliness Standard</strong></div>
                      <select
                        className={styles.input}
                        value={rooms[activeRoom].cleanliness || ''}
                        onChange={(e) => {
                          const updatedRooms = [...rooms];
                          updatedRooms[activeRoom] = {
                            ...updatedRooms[activeRoom],
                            cleanliness: e.target.value as any
                          };
                          setRooms(updatedRooms);
                        }}
                      >
                        <option value="">Select cleanliness standard...</option>
                        <option value="professionally cleaned">Professionally Cleaned</option>
                        <option value="good domestic standard">Good Domestic Standard</option>
                        <option value="average domestic standard">Average Domestic Standard</option>
                        <option value="poor domestic standard">Poor Domestic Standard</option>
                        <option value="not cleaned">Not Cleaned</option>
                      </select>
                    </div>

                    {/* Room Notes */}
                    <div style={{ marginBottom: '20px' }}>
                      <div className={styles.label}><strong>Notes</strong></div>
                      <textarea
                        className={styles.input}
                        value={rooms[activeRoom].items[0]?.notes || ''}
                        onChange={(e) => {
                          const updatedRooms = [...rooms];
                          // Ensure there's at least one item to store notes
                          if (!updatedRooms[activeRoom].items[0]) {
                            updatedRooms[activeRoom].items = [{ category: '', condition: '', notes: '', photos: [] }];
                          }
                          updatedRooms[activeRoom].items[0].notes = e.target.value;
                          setRooms(updatedRooms);
                          autosave.markAsChanged();
                        }}
                        placeholder="Add any notes about this room..."
                        rows={4}
                        style={{ resize: 'vertical', minHeight: '80px' }}
                      />
                    </div>

                    {/* Room Photos */}
                    <div style={{ marginBottom: '20px' }}>
                      <div className={styles.label} style={{ marginBottom: '12px' }}>
                        <strong>Room Photos</strong>
                      </div>
                      {/* Ensure there's at least one item for photos */}
                      {(() => {
                        if (!rooms[activeRoom].items[0]) {
                          const updatedRooms = [...rooms];
                          updatedRooms[activeRoom].items = [{ category: '', condition: '', notes: '', photos: [] }];
                          setRooms(updatedRooms);
                        }
                        return null;
                      })()}
                      
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        {renderRoomCameraButton(activeRoom, 0)}
                        {renderRoomUploadButton(activeRoom, 0)}
                      </div>
                      
                      {rooms[activeRoom].items[0]?.photos && rooms[activeRoom].items[0].photos.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px', marginTop: '12px' }}>
                          {rooms[activeRoom].items[0].photos.map((photo) => (
                            <div key={photo.id} style={{ position: 'relative', paddingTop: '100%', background: '#f3f4f6', borderRadius: '8px', overflow: 'hidden' }}>
                              <img 
                                src={photo.url || photo.localPath} 
                                alt="Room" 
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <button
                                onClick={() => {
                                  const updatedRooms = [...rooms];
                                  if (updatedRooms[activeRoom].items[0]) {
                                    updatedRooms[activeRoom].items[0].photos = 
                                      updatedRooms[activeRoom].items[0].photos?.filter(p => p.id !== photo.id) || [];
                                    setRooms(updatedRooms);
                                    autosave.markAsChanged();
                                  }
                                }}
                                style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(239, 68, 68, 0.9)', color: 'white', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', fontSize: '12px' }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Regular Check-In/Check-Out: Full inventory with items */
                  <>
                    {rooms[activeRoom].items.map((item, itemIndex) => (
                      <RoomInventoryItem
                        key={itemIndex}
                        item={item}
                        roomIndex={activeRoom}
                        itemIndex={itemIndex}
                        updateRoomItem={updateRoomItem}
                        removeItemFromRoom={removeItemFromRoom}
                        addItemAfter={addItemAfter}
                        renderCameraButtonProp={() => renderRoomCameraButton(activeRoom, itemIndex)}
                        renderGalleryButtonProp={() => renderRoomUploadButton(activeRoom, itemIndex)}
                        isProcessing={isProcessing(activeRoom, itemIndex)}
                      />
                    ))}
                    
                    <button
                      onClick={() => addItemToRoom(activeRoom)}
                      className={styles.emptyState}
                      type="button"
                      style={{ 
                        marginTop: '16px',
                        cursor: 'pointer',
                        justifyContent: 'center',
                        fontWeight: '700'
                      }}
                    >
                      <Plus size={18} style={{ marginRight: '8px' }} /> Add Item to {rooms[activeRoom].name || `Room ${activeRoom + 1}`}
                    </button>
                  </>
                )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Keys Section */}
          <div className={styles.card} style={{ marginTop: '18px' }}>
            <div className={styles.cardHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <div className={styles.badge + ' ' + styles.badgeYellow}>🔑</div>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ margin: 0, fontSize: '16px' }}>Keys</h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pi-muted)' }}>Document all keys handed over.</p>
                </div>
              </div>
              <button className={styles.btn} onClick={addKeyEntry} type="button">
                <Plus size={16} style={{ marginRight: '4px' }} /> Add Key
              </button>
            </div>
            <div className={styles.cardBody}>
            {keysData.map((keyEntryData, index) => (
        <div key={index} className="mb-6">
          <KeyEntryItem
                keyEntry={keyEntryData}
          index={index}
          updateKeyEntry={updateKeyEntry}
          removeKeyEntry={removeKeyEntry}
        />
          <SectionPhotoGallery
            photos={keyEntryData.photos || []}
            onCameraClick={() => {
              handleSectionPhotoCapture(
                (photos) => updateKeyPhotos(index, photos),
                keyEntryData.photos || []
              );
            }}
            onUploadClick={(e) => {
              handleSectionPhotoUpload(
                (photos) => updateKeyPhotos(index, photos),
                keyEntryData.photos || [],
                e
              );
            }}
            onDeletePhoto={(photoId) => deleteKeyPhoto(index, photoId)}
            sectionLabel={`Photos for ${keyEntryData.handedOver || 'Key'}`}
          />
        </div>
      ))}
            </div>
          </div>

          {/* Certificates Section */}
          <div className={styles.card} style={{ marginTop: '18px' }}>
            <div className={styles.cardHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <div className={styles.badge + ' ' + styles.badgeGreen}>✅</div>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ margin: 0, fontSize: '16px' }}>Certificates</h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pi-muted)' }}>Gas and electrical safety certificates.</p>
                </div>
              </div>
            </div>
            <div className={styles.cardBody}>
              {/* Gas Certificate */}
              <div style={{ padding: '16px', background: 'rgba(248, 250, 252, 0.5)', borderRadius: '16px', border: '1px solid var(--pi-stroke)', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--pi-text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%' }}></span>
                  Gas Safety Certificate
                </h3>
                <div className={styles.row + ' ' + styles.cols2}>
                  <CustomDateInput
                    id="gasCertDate"
                    label="Expiry Date"
                    value={certificates.gas.date}
                    onChange={(value) => handleCertificateChange('gas', 'date', value)}
                    />
                    <CustomInput
                    id="gasCertNumber"
                      label="Certificate Number"
                    value={certificates.gas.number}
                    onChange={(value) => handleCertificateChange('gas', 'number', value)}
                    placeholder="Enter certificate number"
                  />
                </div>
                <SectionPhotoGallery
                  photos={certificates.gas.photos || []}
                  onCameraClick={() => {
                    handleSectionPhotoCapture(
                      (photos) => updateCertificatePhotos('gas', photos),
                      certificates.gas.photos || []
                    );
                  }}
                  onUploadClick={(e) => {
                    handleSectionPhotoUpload(
                      (photos) => updateCertificatePhotos('gas', photos),
                      certificates.gas.photos || [],
                      e
                    );
                  }}
                  onDeletePhoto={(photoId) => deleteCertificatePhoto('gas', photoId)}
                  sectionLabel="Gas Certificate Photos"
                />
              </div>
              {/* Electrical Certificate */}
              <div style={{ padding: '16px', background: 'rgba(248, 250, 252, 0.5)', borderRadius: '16px', border: '1px solid var(--pi-stroke)' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--pi-text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%' }}></span>
                  Electrical Safety Certificate
                </h3>
                  <div className={styles.row + ' ' + styles.cols2}>
                    <CustomDateInput
                    id="elecCertDate"
                    label="Expiry Date"
                      value={certificates.electrical.date}
                    onChange={(value) => handleCertificateChange('electrical', 'date', value)}
                    />
                    <CustomInput
                    id="elecCertNumber"
                      label="Certificate Number"
                      value={certificates.electrical.number}
                    onChange={(value) => handleCertificateChange('electrical', 'number', value)}
                    placeholder="Enter certificate number"
                    />
            </div>
                <SectionPhotoGallery
                  photos={certificates.electrical.photos || []}
                  onCameraClick={() => {
                    handleSectionPhotoCapture(
                      (photos) => updateCertificatePhotos('electrical', photos),
                      certificates.electrical.photos || []
                    );
                  }}
                  onUploadClick={(e) => {
                    handleSectionPhotoUpload(
                      (photos) => updateCertificatePhotos('electrical', photos),
                      certificates.electrical.photos || [],
                      e
                    );
                  }}
                  onDeletePhoto={(photoId) => deleteCertificatePhoto('electrical', photoId)}
                  sectionLabel="EICR Certificate Photos"
                />
                </div>
          </div>
            </div>
            
          {/* Meter Readings Section */}
          <div className={styles.card} style={{ marginTop: '18px' }}>
            <div className={styles.cardHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <div className={styles.badge + ' ' + styles.badgePurple}>⚡</div>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ margin: 0, fontSize: '16px' }}>Meter Readings</h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--pi-muted)' }}>Gas, electricity, and water meters.</p>
                </div>
              </div>
            </div>
            <div className={styles.cardBody}>
              {/* Gas Meter */}
              <div style={{ padding: '16px', background: 'rgba(248, 250, 252, 0.5)', borderRadius: '16px', border: '1px solid var(--pi-stroke)', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--pi-text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%' }}></span>
                  Gas Meter
                </h3>
                <div className={styles.row + ' ' + styles.cols2} style={{ marginBottom: '12px' }}>
                  <CustomInput
                    id="gasMeterReading"
                    label="Reading"
                    value={meterReadings.gas.reading}
                    onChange={(value) => handleMeterReadingChange('gas', 'reading', value)}
                    placeholder="Enter reading"
                  />
                  <CustomInput
                    id="gasMeterSerial"
                    label="Serial Number"
                    value={meterReadings.gas.serialNo}
                    onChange={(value) => handleMeterReadingChange('gas', 'serialNo', value)}
                    placeholder="Enter serial number"
                  />
                  <CustomInput
                    id="gasMeterLocation"
                    label="Location"
                    value={meterReadings.gas.location}
                    onChange={(value) => handleMeterReadingChange('gas', 'location', value)}
                    placeholder="e.g., Under stairs"
                    className="md:col-span-2" // Span across two columns on medium screens and up
                  />
                </div>
                <SectionPhotoGallery
                  photos={meterReadings.gas.photos || []}
                  onCameraClick={() => {
                    handleSectionPhotoCapture(
                      (photos) => updateMeterPhotos('gas', photos),
                      meterReadings.gas.photos || []
                    );
                  }}
                  onUploadClick={(e) => {
                    handleSectionPhotoUpload(
                      (photos) => updateMeterPhotos('gas', photos),
                      meterReadings.gas.photos || [],
                      e
                    );
                  }}
                  onDeletePhoto={(photoId) => deleteMeterPhoto('gas', photoId)}
                  sectionLabel="Gas Meter Photos"
                />
              </div>
              {/* Electricity Meter */}
              <div style={{ padding: '16px', background: 'rgba(248, 250, 252, 0.5)', borderRadius: '16px', border: '1px solid var(--pi-stroke)', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--pi-text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', background: '#eab308', borderRadius: '50%' }}></span>
                  Electricity Meter
                </h3>
                <div className={styles.row + ' ' + styles.cols2} style={{ marginBottom: '12px' }}>
                      <CustomInput
                    id="elecMeterReading"
                        label="Reading"
                    value={meterReadings.electricity.reading}
                    onChange={(value) => handleMeterReadingChange('electricity', 'reading', value)}
                    placeholder="Enter reading"
                      />
                      <CustomInput
                    id="elecMeterSerial"
                    label="Serial Number"
                    value={meterReadings.electricity.serialNo}
                    onChange={(value) => handleMeterReadingChange('electricity', 'serialNo', value)}
                    placeholder="Enter serial number"
                  />
                  <CustomInput
                    id="elecMeterLocation"
                        label="Location"
                    value={meterReadings.electricity.location}
                    onChange={(value) => handleMeterReadingChange('electricity', 'location', value)}
                    placeholder="e.g., Hallway cupboard"
                    className="md:col-span-2"
                  />
                </div>
                <SectionPhotoGallery
                  photos={meterReadings.electricity.photos || []}
                  onCameraClick={() => {
                    handleSectionPhotoCapture(
                      (photos) => updateMeterPhotos('electricity', photos),
                      meterReadings.electricity.photos || []
                    );
                  }}
                  onUploadClick={(e) => {
                    handleSectionPhotoUpload(
                      (photos) => updateMeterPhotos('electricity', photos),
                      meterReadings.electricity.photos || [],
                      e
                    );
                  }}
                  onDeletePhoto={(photoId) => deleteMeterPhoto('electricity', photoId)}
                  sectionLabel="Electricity Meter Photos"
                />
              </div>
              {/* Water Meter */}
              <div style={{ padding: '16px', background: 'rgba(248, 250, 252, 0.5)', borderRadius: '16px', border: '1px solid var(--pi-stroke)' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--pi-text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', background: '#06b6d4', borderRadius: '50%' }}></span>
                  Water Meter
                </h3>
                <div className={styles.row + ' ' + styles.cols2} style={{ marginBottom: '12px' }}>
                      <CustomInput
                    id="waterMeterReading"
                    label="Reading"
                    value={meterReadings.water.reading}
                    onChange={(value) => handleMeterReadingChange('water', 'reading', value)}
                    placeholder="Enter reading"
                      />
                      <CustomInput
                    id="waterMeterSerial"
                        label="Serial Number"
                    value={meterReadings.water.serialNo}
                    onChange={(value) => handleMeterReadingChange('water', 'serialNo', value)}
                    placeholder="Enter serial number"
                  />
                  <CustomInput
                    id="waterMeterLocation"
                    label="Location"
                    value={meterReadings.water.location}
                    onChange={(value) => handleMeterReadingChange('water', 'location', value)}
                    placeholder="e.g., Outside front"
                    className="md:col-span-2"
                  />
                </div>
                <SectionPhotoGallery
                  photos={meterReadings.water.photos || []}
                  onCameraClick={() => {
                    handleSectionPhotoCapture(
                      (photos) => updateMeterPhotos('water', photos),
                      meterReadings.water.photos || []
                    );
                  }}
                  onUploadClick={(e) => {
                    handleSectionPhotoUpload(
                      (photos) => updateMeterPhotos('water', photos),
                      meterReadings.water.photos || [],
                      e
                    );
                  }}
                  onDeletePhoto={(photoId) => deleteMeterPhoto('water', photoId)}
                  sectionLabel="Water Meter Photos"
                />
              </div>
                              </div>
                </div>

          {/* Restored Signature Section */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg mt-8 border border-yellow-200">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="bg-gradient-to-br from-yellow-400 to-amber-500 p-2 rounded-lg mr-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              Signatures
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Assessor Signature</h3>
                  {signatures.assessor ? (
                  <img src={signatures.assessor} alt="Assessor Signature" className="border rounded-lg h-32 object-contain bg-white shadow-sm" />
                ) : (
                  <p className="text-sm text-gray-500 italic">No signature provided.</p>
                )}
                       <button
                  onClick={() => openSignatureModal('assessor')}
                  className="mt-3 px-4 py-2 text-sm font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-all duration-200 border border-yellow-300 hover:border-yellow-400"
                >
                  {signatures.assessor ? 'Re-sign' : 'Add Signature'}
                      </button>
                    </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Customer Signature</h3>
                {signatures.customer ? (
                  <img src={signatures.customer} alt="Customer Signature" className="border rounded-lg h-32 object-contain bg-white shadow-sm" />
                  ) : (
                  <p className="text-sm text-gray-500 italic">No signature provided.</p>
                )}
                    <button
                  onClick={() => openSignatureModal('customer')}
                  className="mt-3 px-4 py-2 text-sm font-medium bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all duration-200 border border-blue-300 hover:border-blue-400"
                    >
                  {signatures.customer ? 'Re-sign' : 'Add Signature'}
                    </button>
                </div>
              </div>
             <div className="mt-8 text-center">
                <button
                    onClick={handleExportClick}
                    className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-bold rounded-xl hover:from-yellow-500 hover:to-amber-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center mx-auto gap-3"
                >
                    <Download size={20} />
                    Export Inventory
                </button>
              </div>
          </div>
          {/* End Restored Signature Section */}

        </main>
        </div>
      {/* Signature Modal */}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={handleSaveSignature}
        onClear={handleClearSignature}
        title={`Provide ${currentSignatureType.charAt(0).toUpperCase() + currentSignatureType.slice(1)} Signature`}
        signatureRef={
          currentSignatureType === 'assessor' ? assessorSignatureRef :
          currentSignatureType === 'customer' ? customerSignatureRef :
          currentSignatureType === 'tenant' ? tenantSignatureRef : landlordSignatureRef // Adjust ref based on type
        }
      />
      {/* Final Signature Modal */}
       <FinalSignatureModal
        isOpen={isFinalSignatureModalOpen}
        onClose={closeFinalSignatureModal}
        onSave={handleSaveFinalSignature} // Ensure you have a handler for this
        onClear={handleClearFinalSignature}   // And for this
        title="Final Review & Signatures"
        signatureRef={finalSignatureModalRef} // Dedicated ref for this modal
      />

      
      {/* Export Modal - 2-Step with Customization */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/20">
            <div className="relative overflow-hidden rounded-t-2xl">
              <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-700 p-6">
                <div className="flex items-center justify-between relative z-10">
                  <div>
                    <h2 className="text-xl font-bold text-white mb-1">Export Inventory</h2>
                    <p className="text-teal-100 text-sm">Customize and export</p>
                  </div>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
              {/* Step Progress */}
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className={`flex items-center space-x-2 ${modalStep === 'customization' ? 'text-teal-600' : 'text-green-600'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    modalStep === 'customization' ? 'bg-teal-100 text-teal-600' : 'bg-green-100 text-green-600'
                  }`}>
                    {modalStep === 'export' ? '✓' : '1'}
                  </div>
                  <span className="text-sm font-medium">Customize</span>
                </div>
                <div className={`w-12 h-0.5 ${modalStep === 'export' ? 'bg-green-200' : 'bg-gray-200'}`}></div>
                <div className={`flex items-center space-x-2 ${modalStep === 'export' ? 'text-teal-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    modalStep === 'export' ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    2
                  </div>
                  <span className="text-sm font-medium">Export</span>
                </div>
              </div>

              {/* Step 1: Customization */}
              {modalStep === 'customization' && isStandardPlan && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <Settings className="w-4 h-4" />
                      <p className="text-sm font-medium">Customization is not available on the Standard plan</p>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">Upgrade to Pro to customize logos, colors, and company details in your exports.</p>
                  </div>
                  
                  {/* Next Button for Standard Users */}
                  <div className="pt-2">
                    <button
                      onClick={() => setModalStep('export')}
                      className="w-full py-3 px-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-medium rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <span>Next: Export Options</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
              
              {modalStep === 'customization' && !isStandardPlan && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                  {/* Saved Profiles */}
                  {savedProfiles.length > 0 && (
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-700">Saved Profiles</label>
                        <span className="text-[10px] text-gray-500">{savedProfiles.length} saved</span>
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {savedProfiles.map(profile => (
                          <div key={profile.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                            {profile.logoDataUrl && (
                              <img src={profile.logoDataUrl} alt="" className="h-6 w-auto object-contain" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-900 truncate">{profile.name}</div>
                              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: profile.headerColor }}></div>
                                <span className="truncate">{profile.companyName}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => loadProfile(profile)}
                              className="px-2 py-1 text-[10px] font-medium text-teal-600 hover:bg-teal-50 rounded transition-colors"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => deleteProfile(profile.id)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Save Current Profile */}
                  {!showSaveProfile ? (
                    <button
                      onClick={() => setShowSaveProfile(true)}
                      className="w-full px-3 py-2 text-xs font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Current Configuration as Profile
                    </button>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        placeholder="Enter profile name..."
                        className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                        onKeyPress={(e) => e.key === 'Enter' && saveProfile()}
                        autoFocus
                      />
                      <button
                        onClick={saveProfile}
                        className="px-3 py-2 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setShowSaveProfile(false); setProfileName(''); }}
                        className="px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  
                  {/* Logo Upload */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-2 block">Company Logo (Optional)</label>
                    {logoPreview ? (
                      <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded border">
                        <img src={logoPreview} alt="Logo" className="h-8 w-auto object-contain" />
                        <div className="flex-1 text-xs text-gray-500 truncate">
                          {logoFile?.name || 'Logo'}
                        </div>
                        <button
                          onClick={handleRemoveLogo}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center">
                        <input
                          type="file"
                          id="inventory-logo-upload"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="inventory-logo-upload"
                          className="cursor-pointer text-xs text-gray-500 hover:text-gray-700"
                        >
                          Click to upload logo
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Theme Color */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-2 block">Theme Color</label>
                    <button
                      onClick={openColorPicker}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-left border border-gray-300 rounded hover:border-gray-400 transition-colors"
                    >
                      <div 
                        className="w-5 h-5 rounded border border-gray-300 shadow-inner"
                        style={{ backgroundColor: headerColor }}
                      ></div>
                      <span className="text-sm font-mono">{headerColor}</span>
                      <span className="text-xs text-gray-500 ml-auto">Click to change</span>
                    </button>
                    <div className="text-[10px] text-gray-500 mt-1 pl-1">
                      Applied to headers & accent elements
                    </div>
                  </div>

                  {/* Company Details */}
                  <div className="space-y-3 pt-2 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-700 mb-2">Company Details</div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Company Name</label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Energy Ltd"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Website</label>
                      <input
                        type="text"
                        value={companyWebsite}
                        onChange={(e) => setCompanyWebsite(e.target.value)}
                        placeholder="www.yourcompany.com"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Phone Number</label>
                      <input
                        type="text"
                        value={companyPhone}
                        onChange={(e) => setCompanyPhone(e.target.value)}
                        placeholder="0800 123 4567"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Email Address</label>
                      <input
                        type="email"
                        value={companyEmail}
                        onChange={(e) => setCompanyEmail(e.target.value)}
                        placeholder="info@yourcompany.com"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Address</label>
                      <input
                        type="text"
                        value={companyAddress}
                        onChange={(e) => setCompanyAddress(e.target.value)}
                        placeholder="123 Business Street, City, Postcode"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => setModalStep('export')}
                    className="w-full py-3 px-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-medium rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-200"
                  >
                    Next: Choose Format →
                  </button>
                </div>
              )}

              {/* Step 2: Export Format */}
              {modalStep === 'export' && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                  <button
                    onClick={() => setModalStep('customization')}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1 mb-2"
                  >
                    <span>← Back</span>
                  </button>
                  
                  <button
                    onClick={() => { setExportFormat('html'); handleExport('html', { logoDataUrl: logoPreview || undefined, headerColor, companyName, companyWebsite, companyPhone, companyEmail, companyAddress }); }}
                    className="w-full p-4 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-xl transition-all text-left shadow-lg"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Export Interactive HTML</span>
                      <span className="px-2 py-0.5 bg-white/25 rounded text-xs font-bold">✨</span>
                    </div>
                    <div className="text-xs text-teal-100 mt-1">Offline • Clickable photos • Generate PDF from HTML</div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Color Picker Modal */}
      {showColorPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Choose Color</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={tempColor}
                  onChange={(e) => setTempColor(e.target.value)}
                  className="w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={tempColor}
                  onChange={(e) => setTempColor(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono"
                  placeholder="#f59e0b"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={cancelColorChange}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyColor}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading Modal */}
      <InventoryLoadingModal 
        isOpen={isInventoryLoading} 
        loadingStage={loadingStage}
      />
      
      {/* Section Photo Capture with ContinuousCamera */}
      {isSectionCameraOpen && (
        <ContinuousCamera
          isOpen={isSectionCameraOpen}
          onClose={handleSectionCameraClose}
          onPhotosCaptured={handleSectionPhotosCaptured}
          maxPhotos={30}
        />
      )}
    </ErrorBoundary>
  );
};

export default PropertyInventory;